/**
 * India Public Health & Disease Surveillance Registry
 * Primary Sources (Government of India / MoHFW):
 * 1. data.gov.in (Open Government Data Platform India) - National List of Essential Medicines (NLEM), CDSCO approvals
 * 2. CBHI (Central Bureau of Health Intelligence) - National Health Profile (NHP) district & state disease statistics
 * 3. IDSP (Integrated Disease Surveillance Programme / NCDC) - Weekly epidemic outbreak surveillance (Forms S, P, L)
 *
 * Integrated as the primary/main clinical and epidemiological reference for generic drugs and endemic disease protocols.
 */

// Central Bureau of Health Intelligence (CBHI) - National Health Profile Disease Statistics
export const CBHI_DISEASE_PROFILES = {
  dengue: {
    disease: 'Dengue Fever / Severe Dengue (DHF/DSS)',
    icd10: 'A90 / A91',
    vector: 'Aedes aegypti (day-biting mosquito)',
    annualCasesIndia: '180,000 – 250,000 reported cases annually (NHP / CBHI)',
    caseFatalityRate: '0.1% – 0.5% with early fluid management; up to 5% in severe shock (DSS)',
    highPrevalenceStates: ['Punjab', 'Rajasthan', 'West Bengal', 'Tamil Nadu', 'Karnataka', 'Delhi', 'Maharashtra'],
    peakTransmission: 'Monsoon and post-monsoon months (July – November)',
    cbhiReference: 'CBHI National Health Profile, Communicable Disease Statistics (Chapter 3)',
    firstLineGenerics: ['paracetamol', 'oral rehydration salts', 'intravenous fluids (crystalloids)'],
    contraindicatedGenerics: ['aspirin', 'ibuprofen', 'diclofenac'], // Risk of hemorrhage / platelet dysfunction
    clinicalPearls: 'Avoid NSAIDs due to platelet inhibition and GI bleeding risk. Monitor hematocrit and platelet count daily.',
  },
  malaria: {
    disease: 'Malaria (Plasmodium falciparum & Plasmodium vivax)',
    icd10: 'B50 / B51',
    vector: 'Anopheles culicifacies / Anopheles stephensi',
    annualCasesIndia: '~180,000 reported cases annually under NVBDCP; declining under National Malaria Elimination Plan',
    caseFatalityRate: '< 0.05% nationally; high risk in cerebral malaria (P. falciparum)',
    highPrevalenceStates: ['Odisha', 'Chhattisgarh', 'Jharkhand', 'Madhya Pradesh', 'Tripura', 'Mizoram'],
    peakTransmission: 'Perennial in tribal forested belts; post-monsoon peaks (July – October)',
    cbhiReference: 'National Vector Borne Disease Control Programme (NVBDCP) / CBHI NHP',
    firstLineGenerics: ['artesunate', 'artemether', 'lumefantrine', 'chloroquine', 'primaquine'],
    contraindicatedGenerics: ['primaquine in G6PD deficiency without screening'],
    clinicalPearls: 'Artemisinin-based Combination Therapy (ACT) is mandatory for P. falciparum in India. Chloroquine is first-line for P. vivax.',
  },
  tuberculosis: {
    disease: 'Pulmonary & Extrapulmonary Tuberculosis (TB)',
    icd10: 'A15 / A16',
    etiology: 'Mycobacterium tuberculosis',
    annualCasesIndia: 'Estimated 2.4 – 2.6 million incident cases annually (highest worldwide; ~27% of global burden)',
    notificationRate: '~172 per 100,000 population notified under Ni-kshay / NTEP',
    highPrevalenceStates: ['Uttar Pradesh', 'Bihar', 'Madhya Pradesh', 'Rajasthan', 'Maharashtra', 'West Bengal'],
    cbhiReference: 'National Tuberculosis Elimination Program (NTEP) / CBHI NHP Health Indicators',
    firstLineGenerics: ['rifampicin', 'isoniazid', 'pyrazinamide', 'ethambutol', 'pyridoxine'],
    secondLineGenerics: ['bedaquiline', 'linezolid', 'delamanid', 'moxifloxacin', 'levofloxacin'],
    clinicalPearls: 'Administered under NTEP daily FDC regimen. Co-administer pyridoxine (vitamin B6) with isoniazid to prevent peripheral neuropathy.',
  },
  'acute-diarrheal-disease': {
    disease: 'Acute Diarrhoeal Diseases (ADD) & Cholera',
    icd10: 'A09 / A00',
    etiology: 'Rotavirus, E. coli, Vibrio cholerae, Shigella, Salmonella',
    annualCasesIndia: '~10.5 – 12 million reported episodes annually (CBHI NHP)',
    mortalitySignificance: 'Second leading cause of mortality in children under 5 years in high-burden districts',
    highPrevalenceStates: ['Uttar Pradesh', 'Bihar', 'West Bengal', 'Assam', 'Odisha'],
    peakTransmission: 'Summer and flood/monsoon periods (water contamination)',
    cbhiReference: 'CBHI Annual Health Profile, Morbidity & Mortality under Communicable Disease Reports',
    firstLineGenerics: ['oral rehydration salts', 'zinc sulfate', 'ciprofloxacin', 'azithromycin', 'metronidazole'],
    clinicalPearls: 'Low-osmolarity ORS + Zinc supplementation (20 mg/day for 14 days) is the cornerstone of Indian child survival strategy.',
  },
  typhoid: {
    disease: 'Typhoid (Enteric Fever)',
    icd10: 'A01',
    etiology: 'Salmonella enterica serovar Typhi & Paratyphi',
    annualCasesIndia: '~1.8 – 2.2 million cases reported annually (CBHI NHP)',
    endemicity: 'Hyperendemic across urban informal settlements and semi-urban districts',
    highPrevalenceStates: ['Maharashtra', 'Uttar Pradesh', 'Tamil Nadu', 'Delhi', 'West Bengal'],
    cbhiReference: 'Central Bureau of Health Intelligence NHP Chapter 3',
    firstLineGenerics: ['ceftriaxone', 'azithromycin', 'ciprofloxacin', 'cefixime'],
    clinicalPearls: 'Fluoroquinolone resistance is widespread in India (~70%). Ceftriaxone or Azithromycin is empirical drug of choice.',
  },
  'respiratory-infections': {
    disease: 'Acute Respiratory Infections (ARI) & Pneumonia',
    icd10: 'J06 / J18',
    etiology: 'Streptococcus pneumoniae, RSV, Influenza A/B, Klebsiella pneumoniae',
    annualCasesIndia: '~35 – 40 million episodes reported annually; single largest cause of child OPD attendance',
    highPrevalenceStates: ['National burden across all Indian states and Union Territories'],
    peakTransmission: 'Winter months (November – February) exacerbated by seasonal air quality index (AQI) drops',
    cbhiReference: 'CBHI Communicable Disease Morbidity Records',
    firstLineGenerics: ['amoxicillin', 'amoxicillin and clavulanate potassium', 'azithromycin', 'ceftriaxone', 'salbutamol'],
    clinicalPearls: 'Amoxicillin is the WHO/India IMNCI first-line oral antibiotic for fast-breathing non-severe pneumonia in children.',
  },
  hypertension: {
    disease: 'Essential Hypertension',
    icd10: 'I10',
    burdenIndia: 'Prevalence ~28.5% in adults (ICMR-INDIAB study & CBHI NHP); ~200 million individuals',
    controlRate: 'Less than 15% of hypertensive individuals in India have blood pressure controlled to target',
    flagshipInitiative: 'India Hypertension Control Initiative (IHCI) / National Programme for Prevention & Control of NCDs (NP-NCD)',
    cbhiReference: 'CBHI Non-Communicable Disease Surveillance Data',
    firstLineGenerics: ['amlodipine', 'telmisartan', 'losartan', 'enalapril', 'chlorthalidone', 'hydrochlorothiazide'],
    clinicalPearls: 'Standard IHCI protocol: Start with Amlodipine 5mg; titrate or combine with Telmisartan 40mg at Primary Health Centre (PHC).',
  },
  diabetes: {
    disease: 'Type 2 Diabetes Mellitus',
    icd10: 'E11',
    burdenIndia: '~101 million individuals living with diabetes, ~136 million with prediabetes (ICMR-INDIAB 2023)',
    epidemiologicalProfile: 'Early onset at younger age and lower BMI in South Asians; high risk of diabetic kidney disease and retinopathy',
    flagshipInitiative: 'National Health Mission (NHM) Universal Non-Communicable Disease Screening',
    cbhiReference: 'CBHI NHP Chronic Disease Indicators',
    firstLineGenerics: ['metformin', 'glimepiride', 'insulin human', 'vildagliptin', 'dapagliflozin'],
    clinicalPearls: 'Metformin 500–2000 mg/day remains the first-line pharmacotherapy under NLEM and Indian ICMR guidelines.',
  },
  chikungunya: {
    disease: 'Chikungunya Fever',
    icd10: 'A92.0',
    vector: 'Aedes aegypti / Aedes albopictus',
    annualCasesIndia: '80,000 – 120,000 suspected / confirmed cases annually (NVBDCP / CBHI)',
    caseFatalityRate: '< 0.1%; notable for chronic, debilitating polyarthralgia',
    highPrevalenceStates: ['Karnataka', 'Maharashtra', 'Gujarat', 'Tamil Nadu', 'Madhya Pradesh'],
    peakTransmission: 'Post-monsoon months (August – November)',
    cbhiReference: 'CBHI National Health Profile, Vector-Borne Communicable Diseases',
    firstLineGenerics: ['paracetamol', 'chloroquine'],
    contraindicatedGenerics: ['aspirin', 'ibuprofen (until dengue excluded)'],
    clinicalPearls: 'Severe incapacitating joint pain ("that which bends up"). Avoid aspirin. Rule out dengue before starting NSAIDs.',
  },
  'kala-azar': {
    disease: 'Kala-azar (Visceral Leishmaniasis / Black Fever)',
    icd10: 'B55.0',
    vector: 'Phlebotomus argentipes (sandfly)',
    annualCasesIndia: '< 1,000 cases annually (under National Kala-azar Elimination Programme)',
    caseFatalityRate: '> 90% if untreated; < 1% with single-dose liposomal amphotericin B',
    highPrevalenceStates: ['Bihar', 'Jharkhand', 'West Bengal', 'Eastern Uttar Pradesh'],
    peakTransmission: 'Summer and pre-monsoon (April – June)',
    cbhiReference: 'National Kala-azar Elimination Programme / CBHI NHP',
    firstLineGenerics: ['amphotericin b', 'miltefosine', 'paromomycin'],
    clinicalPearls: 'Triad: prolonged fever, marked splenomegaly, and severe weight loss with hyperpigmentation. Single-dose Liposomal Amphotericin B (10 mg/kg) is first-line national regimen.',
  },
  'japanese-encephalitis': {
    disease: 'Japanese Encephalitis (JE) / Acute Encephalitis Syndrome (AES)',
    icd10: 'A83.0',
    vector: 'Culex tritaeniorhynchus / Culex vishnui (breeding in irrigated paddy fields)',
    annualCasesIndia: '1,200 – 2,500 reported cases annually; ~10,000 AES cases (CBHI NHP)',
    caseFatalityRate: '20% – 30%; 30% – 50% of survivors suffer permanent neuropsychiatric disability',
    highPrevalenceStates: ['Uttar Pradesh (Gorakhpur belt)', 'Assam', 'Bihar', 'Tamil Nadu', 'West Bengal'],
    peakTransmission: 'Monsoon and post-monsoon (July – October)',
    cbhiReference: 'NVBDCP / CBHI National Health Profile Chapter 3',
    firstLineGenerics: ['mannitol', 'phenytoin', 'paracetamol', 'ceftriaxone'],
    clinicalPearls: 'Sudden high fever, convulsions, and altered sensorium. Critical supportive care: control intracranial hypertension with 20% mannitol and secure airway.',
  },
  'scrub-typhus': {
    disease: 'Scrub Typhus (Orientia tsutsugamushi)',
    icd10: 'A75.3',
    vector: 'Leptotrombidium deliense (larval trombiculid mites / chiggers)',
    annualCasesIndia: '~40,000+ cases recognized annually across sub-Himalayan and southern scrub regions (IDSP/NCDC)',
    caseFatalityRate: '1% – 5% if treated promptly; up to 30% in multi-organ dysfunction syndrome',
    highPrevalenceStates: ['Himachal Pradesh', 'Uttarakhand', 'Tamil Nadu', 'Meghalaya', 'Kerala', 'Rajasthan'],
    peakTransmission: 'Post-monsoon period (August – November)',
    cbhiReference: 'Integrated Disease Surveillance Programme (IDSP) Outbreak Reports',
    firstLineGenerics: ['doxycycline', 'azithromycin'],
    clinicalPearls: 'Meticulously search for the diagnostic pathognomonic "eschar" in skin folds and groin. Doxycycline (100 mg BID for 7 days) induces rapid defervescence within 48 hours.',
  },
  leptospirosis: {
    disease: 'Leptospirosis (Weil Disease)',
    icd10: 'A27',
    etiology: 'Leptospira interrogans (contact with floodwaters contaminated with rodent urine)',
    annualCasesIndia: '5,000 – 10,000 reported cases during monsoon urban and coastal floods',
    caseFatalityRate: '5% – 15% in severe icteric disease with pulmonary hemorrhage or acute kidney injury',
    highPrevalenceStates: ['Kerala', 'Gujarat', 'Tamil Nadu', 'Maharashtra (Mumbai)', 'Karnataka'],
    peakTransmission: 'Heavy monsoon flooding (June – October)',
    cbhiReference: 'National Centre for Disease Control (NCDC) Zoonoses Division / CBHI NHP',
    firstLineGenerics: ['doxycycline', 'amoxicillin', 'ceftriaxone'],
    clinicalPearls: 'Severe conjunctival suffusion without purulent exudate is a hallmark sign. Post-flood chemoprophylaxis with Doxycycline 200 mg single weekly dose in flood zones.',
  },
  rabies: {
    disease: 'Rabies (Hydrophobia)',
    icd10: 'A82',
    etiology: 'Lyssavirus (transmitted via saliva of rabid mammals, primarily stray dogs)',
    annualCasesIndia: '~18,000 – 20,000 human deaths annually (~36% of global burden; CBHI / NRCP)',
    caseFatalityRate: '~100% once clinical symptoms develop',
    highPrevalenceStates: ['Uttar Pradesh', 'West Bengal', 'Bihar', 'Madhya Pradesh', 'Maharashtra'],
    cbhiReference: 'National Rabies Control Programme (NRCP) / CBHI NHP Mortality Records',
    firstLineGenerics: ['rabies vaccine', 'rabies immunoglobulin'],
    clinicalPearls: '100% fatal once symptomatic, but 100% preventable with immediate Post-Exposure Prophylaxis (PEP). Immediate wound washing under running tap water with soap for 15 minutes, followed by intradermal rabies vaccination (Day 0, 3, 7, 28) and RIG infiltration into Category III wounds.',
  },
  'acute-mi': {
    disease: 'Acute Myocardial Infarction (STEMI / NSTEMI)',
    icd10: 'I21',
    annualCasesIndia: '~3.2 million coronary events annually; leading cause of premature mortality <55 yrs (CBHI / ICMR)',
    caseFatalityRate: '6% – 10% in hospital with rapid reperfusion; up to 30% without primary PCI or fibrinolysis',
    highPrevalenceStates: ['Kerala', 'Punjab', 'Tamil Nadu', 'Maharashtra', 'West Bengal'],
    cbhiReference: 'National Programme for Prevention & Control of NCDs (NP-NCD) / CBHI NHP',
    firstLineGenerics: ['aspirin', 'clopidogrel', 'atorvastatin', 'metoprolol', 'streptokinase', 'nitroglycerin'],
    clinicalPearls: 'Emergency golden hour: Immediate dual antiplatelet loading (Aspirin 300 mg + Clopidogrel 300-600 mg) + Atorvastatin 80 mg stat. Door-to-balloon PCI <90 min or door-to-needle thrombolysis <30 min.',
  },
  stroke: {
    disease: 'Acute Ischemic Stroke & Intracerebral Hemorrhage',
    icd10: 'I63 / I61',
    annualCasesIndia: 'Estimated 1.5 – 1.8 million new stroke cases annually; 1-month case fatality ~20% (CBHI NHP)',
    highPrevalenceStates: ['West Bengal', 'Assam', 'Odisha', 'Kerala', 'Tamil Nadu'],
    cbhiReference: 'CBHI Non-Communicable Disease Surveillance & ICMR Stroke Registry',
    firstLineGenerics: ['aspirin', 'atorvastatin', 'mannitol', 'labetalol'],
    clinicalPearls: 'Non-contrast head CT within 20 minutes to exclude hemorrhage. IV thrombolysis window within 4.5 hours of last seen normal. Avoid aggressive BP lowering in acute ischemic phase unless BP >220/120 mmHg.',
  },
  'chronic-kidney-disease': {
    disease: 'Chronic Kidney Disease (CKD)',
    icd10: 'N18',
    annualCasesIndia: 'Prevalence ~17.2% in adult Indian population (~150 million individuals; CBHI / PMNDP)',
    flagshipInitiative: 'Pradhan Mantri National Dialysis Programme (PMNDP) providing free hemodialysis across district hospitals',
    cbhiReference: 'CBHI NHP Renal Morbidity Statistics & PMNDP Registry',
    firstLineGenerics: ['enalapril', 'telmisartan', 'furosemide', 'calcium carbonate'],
    clinicalPearls: 'Target proteinuria reduction with ACE inhibitors or ARBs, with strict monitoring of potassium and eGFR. Strictly avoid nephrotoxic NSAIDs.',
  },
  'asthma-copd': {
    disease: 'Chronic Obstructive Pulmonary Disease (COPD) & Bronchial Asthma',
    icd10: 'J44 / J45',
    annualCasesIndia: '~55 million COPD and ~37 million asthma patients; India accounts for ~32% of global COPD DALYs (CBHI / ICMR)',
    highPrevalenceStates: ['Rajasthan', 'Uttar Pradesh', 'Bihar', 'Haryana', 'Delhi'],
    cbhiReference: 'CBHI NHP Chronic Obstructive Lung Disease Morbidity Reports',
    firstLineGenerics: ['salbutamol', 'budesonide', 'ipratropium bromide', 'tiotropium'],
    clinicalPearls: 'Inhaled corticosteroids (Budesonide) + formoterol are foundational controller therapy. Biomass smoke exposure ("chulha") is a major non-tobacco cause of severe COPD in rural Indian women.',
  },
  'hepatitis-b': {
    disease: 'Chronic Viral Hepatitis B',
    icd10: 'B18.1',
    annualCasesIndia: 'Prevalence ~2% – 4% (estimated 37 million chronic carriers; NVHCP / CBHI NHP)',
    flagshipInitiative: 'National Viral Hepatitis Control Program (NVHCP) under NHM providing free diagnostics and antivirals',
    cbhiReference: 'NVHCP / CBHI NHP Communicable Diseases Report',
    firstLineGenerics: ['tenofovir disoproxil fumarate', 'entecavir'],
    clinicalPearls: 'Tenofovir or Entecavir possesses high genetic barrier to resistance under NVHCP. Screen pregnant mothers for HBsAg to administer birth-dose Hepatitis B vaccine + HBIG within 24 hours of delivery.',
  },
};

// Integrated Disease Surveillance Programme (IDSP / NCDC) Surveillance Protocols
export const IDSP_SURVEILLANCE_PROTOCOLS = {
  dengue: {
    surveillanceForm: 'Form P (Presumptive - Medical Officers) & Form L (Laboratory Confirmed - IgM ELISA / NS1)',
    alertThreshold: 'More than 2 cases of acute febrile illness with thrombocytopenia in a village/ward in 1 week',
    reportingCadence: 'Weekly submission to District Surveillance Unit (DSU) by every Monday; immediate alert on cluster',
    epidemicAction: 'Source reduction of Aedes larvae (Temephos), indoor space spraying, fever survey within 500m radius',
  },
  malaria: {
    surveillanceForm: 'Form P (Fever with chills/rigors) & Form L (Peripheral Blood Smear / RDT)',
    alertThreshold: 'Slide Positivity Rate (SPR) > 2% or clustering of fever cases',
    reportingCadence: 'Weekly reporting under IDSP / monthly under NVBDCP',
    epidemicAction: 'Focal indoor residual spraying (IRS), distribution of LLINs, mass radical treatment of confirmed cases',
  },
  tuberculosis: {
    surveillanceForm: 'Presumptive TB register; mandatory digital notification via Ni-kshay portal',
    alertThreshold: 'Cough ≥ 2 weeks with evening pyrexia or hemoptysis',
    reportingCadence: 'Real-time case-based notification by public and private practitioners under Epidemic Diseases Act',
    epidemicAction: 'Contact tracing for all household members, CBNAAT / Truenat molecular testing, chest radiography',
  },
  'acute-diarrheal-disease': {
    surveillanceForm: 'Form S (Syndromic - 3+ loose watery stools/day) & Form L (Stool culture for Vibrio cholerae)',
    alertThreshold: 'Doubling of baseline diarrheal cases or any single death / suspected cholera case',
    reportingCadence: 'Immediate telephone alert to District Surveillance Officer (DSO), followed by Form P/L',
    epidemicAction: 'Superchlorination of drinking water sources (> 0.5 ppm residual chlorine), ORS depots, halogen tablets',
  },
  typhoid: {
    surveillanceForm: 'Form P (Step-ladder fever, relative bradycardia) & Form L (Blood culture / Typhidot)',
    alertThreshold: 'Cluster of 5 or more prolonged fever cases in a locality',
    reportingCadence: 'Weekly IDSP portal submission',
    epidemicAction: 'Pipeline leak detection, municipal water sample testing, sanitary inspection of food vendors',
  },
  'respiratory-infections': {
    surveillanceForm: 'Form S (ILI: Fever >38°C + cough) & Form P (SARI: Requiring hospitalization)',
    alertThreshold: 'Unusual spike in pediatric respiratory admissions or cluster of Severe Acute Respiratory Infections',
    reportingCadence: 'Weekly reporting via IDSP portal to State Surveillance Unit (SSU)',
    epidemicAction: 'Nasopharyngeal swab multiplex RT-PCR, oxygen auditing, isolation ward activation',
  },
  chikungunya: {
    surveillanceForm: 'Form P (Acute fever + joint pain) & Form L (IgM ELISA)',
    alertThreshold: 'Clustering of severe arthralgia cases in a village or urban settlement',
    reportingCadence: 'Weekly IDSP reporting; immediate notification on transmission outbreak',
    epidemicAction: 'Vector source reduction, anti-larval operations, fever surveillance in community',
  },
  'kala-azar': {
    surveillanceForm: 'Form P (Fever >15 days + splenomegaly) & Form L (rK39 dipstick rapid test)',
    alertThreshold: 'Any single confirmed indigenous case in previously non-endemic or eliminated block',
    reportingCadence: 'Monthly reporting under National Kala-azar Elimination Programme',
    epidemicAction: 'Focal indoor residual spraying with synthetic pyrethroid (Alphacypermethrin), active case search',
  },
  'japanese-encephalitis': {
    surveillanceForm: 'Form P (Fever + altered mental status / seizures) & Form L (CSF/serum IgM ELISA)',
    alertThreshold: 'More than 1 case of Acute Encephalitis Syndrome in a block within 1 week',
    reportingCadence: 'Daily reporting during peak transmission season (July – November)',
    epidemicAction: 'Intensive vector control around piggeries, community fogging, emergency referral network',
  },
  'scrub-typhus': {
    surveillanceForm: 'Form P (Acute fever with eschar or lymphadenopathy) & Form L (Weil-Felix / Scrub typhus IgM)',
    alertThreshold: 'Cluster of acute febrile illness with multi-organ involvement during harvest/post-monsoon',
    reportingCadence: 'Weekly IDSP surveillance report',
    epidemicAction: 'Chigger mite habitat clearance, community education on wearing protective clothing, early Doxycycline empiric access',
  },
  leptospirosis: {
    surveillanceForm: 'Form P (Fever + conjunctival suffusion + myalgia) & Form L (MAT / IgM ELISA)',
    alertThreshold: 'Occurrence of 2+ cases of fever with jaundice or conjunctival suffusion post-inundation',
    reportingCadence: 'Daily epidemic monitoring during flood events',
    epidemicAction: 'Prophylactic Doxycycline distribution to rescue workers and residents in flood-affected districts',
  },
  rabies: {
    surveillanceForm: 'Animal Bite Registry (Category I, II, III bites) & Form P/L (Suspected human rabies)',
    alertThreshold: 'Any suspected or confirmed human rabies case',
    reportingCadence: 'Immediate 24-hour mandatory notification under NRCP',
    epidemicAction: 'Trace biting animal for 10-day observation; verify anti-rabies vaccination coverage in community dogs',
  },
  'acute-mi': {
    surveillanceForm: 'NP-NCD cardiovascular event registry; acute coronary syndrome notification at secondary hospitals',
    alertThreshold: 'Cluster of young cardiovascular deaths or spikes in emergency admissions',
    reportingCadence: 'Monthly NP-NCD portal compilation',
    epidemicAction: 'Strengthening hub-and-spoke STEMI networks, tele-ECG integration at Primary Health Centres',
  },
  stroke: {
    surveillanceForm: 'National Stroke Registry (NSR / ICMR) & NP-NCD hospital admission logging',
    alertThreshold: 'Regional disparity in stroke admissions or delayed hospital arrival times',
    reportingCadence: 'Monthly stroke data auditing',
    epidemicAction: 'Fast-track stroke care pathways, thrombolytic medicine prepositioning at District Hospitals',
  },
  'chronic-kidney-disease': {
    surveillanceForm: 'PMNDP online national hemodialysis portal tracking dialyzed patients and vascular access',
    alertThreshold: 'Unusual endemic clusters of CKD of unknown etiology (CKDu)',
    reportingCadence: 'Quarterly state non-communicable disease report',
    epidemicAction: 'Groundwater heavy metal / pesticide screening in endemic CKDu clusters',
  },
  'asthma-copd': {
    surveillanceForm: 'National Clean Air Programme (NCAP) health impact surveillance; AQI acute respiratory trigger monitoring',
    alertThreshold: 'Severe Air Quality Index (>400) spike or sharp rise in respiratory emergency visits',
    reportingCadence: 'Real-time seasonal winter surge tracking',
    epidemicAction: 'Public air quality health advisories, nebulization corners at urban health posts',
  },
  'hepatitis-b': {
    surveillanceForm: 'NVHCP national digital dashboard, blood bank screening deferrals, antenatal HBsAg register',
    alertThreshold: 'Cluster of acute viral hepatitis in dialysis units, surgical clinics, or communities',
    reportingCadence: 'Monthly viral hepatitis surveillance report',
    epidemicAction: 'Injection safety auditing, hemodialysis unit sterilization review, mass vaccination of high-risk contacts',
  },
};

// data.gov.in / CDSCO / MoHFW National List of Essential Medicines (NLEM) Comprehensive Formulary
export const INDIA_NLEM_CATALOG = [
  // ----------------------------------------------------
  // SECTION 1: ANTIMALARIALS & ENDEMIC TROPICAL PROTOCOLS
  // ----------------------------------------------------
  {
    id: 'in-artesunate',
    slug: 'artesunate',
    name: 'Artesunate',
    genericName: 'ARTESUNATE',
    class: 'Artemisinin Derivative / Antimalarial',
    category: 'Antibiotic',
    subCategory: 'Antimalarial',
    routes: ['IV', 'IM', 'Oral'],
    nlemTier: 'Primary, Secondary, Tertiary Healthcare (NLEM Category 6.4)',
    cdscoStatus: 'Approved Schedule H Generic Drug; National NVBDCP First-Line',
    nppaRegulated: true,
    desc: 'Semisynthetic artemisinin derivative for rapid clearance of Plasmodium falciparum parasitemia. National first-line drug for severe and complicated malaria in India.',
    dose: 'IV/IM: 2.4 mg/kg stat, repeated at 12h, 24h, then once daily until oral therapy tolerated (minimum 3 parenteral doses). Followed by full course oral ACT.',
    caution: 'Post-artemisinin delayed hemolysis may occur 1–3 weeks post-treatment. Monitor hemoglobin. Always complete oral partner drug.',
    indications: 'Severe falciparum malaria, cerebral malaria, multi-organ dysfunction malaria, complicated P. vivax malaria under NVBDCP guidelines.',
    dosage: 'Adults and children ≥20 kg: 2.4 mg/kg IV/IM; Children <20 kg: 3.0 mg/kg IV/IM.',
    dosageForms: 'Powder for injection: 60 mg, 120 mg vials; Oral tablets: 50 mg, 100 mg.',
    contraindications: 'No absolute contraindications in severe life-threatening malaria.',
    warnings: 'Must be followed by a full 3-day course of oral ACT once patient can swallow.',
    mechanism: 'Endoperoxide bridge cleaved by intraparasitic heme iron, generating reactive free radicals that damage parasitic membrane lipids.',
    relatedDisease: 'malaria',
    source: 'data.gov.in / NVBDCP / NLEM',
    isIndiaMainApi: true,
  },
  {
    id: 'in-artemether-lumefantrine',
    slug: 'artemether-lumefantrine',
    name: 'Artemether + Lumefantrine',
    genericName: 'ARTEMETHER AND LUMEFANTRINE',
    class: 'Artemisinin-based Combination Therapy (ACT)',
    category: 'Antibiotic',
    subCategory: 'Antimalarial',
    routes: ['Oral'],
    nlemTier: 'Primary, Secondary, Tertiary (NLEM Category 6.4)',
    cdscoStatus: 'Approved Schedule H; First-line ACT across North-Eastern & Central States',
    nppaRegulated: true,
    desc: 'Fixed-dose Artemisinin-based Combination Therapy (ACT) for uncomplicated Plasmodium falciparum malaria.',
    dose: 'Standard 6-dose regimen over 3 days (0, 8, 24, 36, 48, and 60 hours). Adult dose: 4 tablets per dose (20mg/120mg). Take with milk or meal.',
    caution: 'Must be administered with fat-containing food to ensure lumefantrine absorption.',
    indications: 'Uncomplicated Plasmodium falciparum malaria; multi-drug resistant malaria under NVBDCP.',
    dosage: '6-dose schedule over 60 hours based on body weight.',
    dosageForms: 'Fixed-dose tablets: Artemether 20 mg + Lumefantrine 120 mg; Dispersible tablets.',
    contraindications: 'Severe malaria requiring parenteral therapy; congenital long QT syndrome.',
    warnings: 'Complete all 6 doses to avoid recrudescence.',
    mechanism: 'Artemether provides rapid initial parasite reduction; lumefantrine clears residual blood-stage parasites.',
    relatedDisease: 'malaria',
    source: 'data.gov.in / NVBDCP / NLEM',
    isIndiaMainApi: true,
  },
  {
    id: 'in-chloroquine',
    slug: 'chloroquine',
    name: 'Chloroquine Phosphate',
    genericName: 'CHLOROQUINE PHOSPHATE',
    class: '4-Aminoquinoline Antimalarial',
    category: 'Antibiotic',
    subCategory: 'Antimalarial',
    routes: ['Oral'],
    nlemTier: 'Primary, Secondary, Tertiary (NLEM Category 6.4)',
    cdscoStatus: 'Approved Schedule H; First-line for P. vivax across India',
    nppaRegulated: true,
    desc: 'Standard antimalarial for erythrocytic stage of Plasmodium vivax and Plasmodium malariae.',
    dose: 'Total 25 mg base/kg over 3 days (Day 1: 10 mg/kg stat, then 5 mg/kg at 6h; Day 2: 5 mg/kg; Day 3: 5 mg/kg). Must pair with Primaquine.',
    caution: 'Retinopathy with prolonged cumulative doses. Not recommended for P. falciparum due to resistance.',
    indications: 'Plasmodium vivax and Plasmodium malariae infections; rheumatoid arthritis and discoid lupus.',
    dosage: 'Day 1: 600 mg base stat, then 300 mg at 6h; Days 2 & 3: 300 mg base single dose.',
    dosageForms: 'Tablets: Chloroquine Phosphate 250 mg (150 mg base); Syrup: 50 mg base / 5 mL.',
    contraindications: 'Pre-existing retinal maculopathy; hypersensitivity.',
    warnings: 'Always pair with 14-day Primaquine for radical cure of vivax hypnozoites.',
    mechanism: 'Inhibits hematin polymerization in parasite food vacuole, causing accumulation of toxic heme.',
    relatedDisease: 'malaria',
    source: 'data.gov.in / NVBDCP / NLEM',
    isIndiaMainApi: true,
  },
  {
    id: 'in-primaquine',
    slug: 'primaquine',
    name: 'Primaquine Phosphate',
    genericName: 'PRIMAQUINE PHOSPHATE',
    class: '8-Aminoquinoline / Tissue Schizonticide',
    category: 'Antibiotic',
    subCategory: 'Antimalarial',
    routes: ['Oral'],
    nlemTier: 'Primary, Secondary, Tertiary (NLEM Category 6.4)',
    cdscoStatus: 'Approved Schedule H; Radical Cure for P. vivax & Gametocytocide for P. falciparum',
    nppaRegulated: true,
    desc: 'Essential 8-aminoquinoline for radical cure of relapsing Plasmodium vivax malaria by destroying hypnozoites in hepatocytes.',
    dose: 'P. vivax radical cure: 0.25 mg/kg/day (15 mg adult base) daily for 14 days after Chloroquine. P. falciparum: 0.75 mg/kg single dose.',
    caution: 'Causes acute intravascular hemolysis in G6PD deficiency. Screen or monitor urine color.',
    indications: 'Radical cure of Plasmodium vivax and P. ovale; transmission blocking of P. falciparum.',
    dosage: '15 mg base orally once daily with food for 14 consecutive days.',
    dosageForms: 'Tablets: 2.5 mg, 7.5 mg, 15 mg base.',
    contraindications: 'Pregnancy; infants under 6 months; severe G6PD deficiency.',
    warnings: 'Contraindicated in pregnancy because fetal G6PD status cannot be verified.',
    mechanism: 'Generates reactive oxygen species disrupting mitochondrial electron transport in liver stages.',
    relatedDisease: 'malaria',
    source: 'data.gov.in / NVBDCP / NLEM',
    isIndiaMainApi: true,
  },

  // ----------------------------------------------------
  // SECTION 2: ANTITUBERCULARS (NTEP / DOTS REGIMENS)
  // ----------------------------------------------------
  {
    id: 'in-rifampicin',
    slug: 'rifampicin',
    name: 'Rifampicin',
    genericName: 'RIFAMPICIN',
    class: 'Rifamycin / Bacterial RNA Polymerase Inhibitor',
    category: 'Antibiotic',
    subCategory: 'Antitubercular',
    routes: ['Oral', 'IV'],
    nlemTier: 'Primary, Secondary, Tertiary (NLEM Category 6.2.4)',
    cdscoStatus: 'Schedule H; Essential core of National TB Elimination Program (NTEP)',
    nppaRegulated: true,
    desc: 'Potent bactericidal rifamycin antibiotic essential for Tuberculosis (NTEP) and Leprosy (NLEP). Destroys active and slow-metabolizing persistent intracellular bacilli.',
    dose: 'Adults: 10 mg/kg daily (typical 450–600 mg once daily) on an empty stomach. Administered as part of daily FDC regimen.',
    caution: 'Strong inducer of CYP3A4 enzymes; reduces levels of oral contraceptives, statins, and anticoagulants. Causes orange-red discoloration of body fluids.',
    indications: 'Pulmonary and extrapulmonary tuberculosis under NTEP; multibacillary leprosy under NLEP.',
    dosage: 'Weight-banded dosing: <35 kg: 300 mg; 35–49 kg: 450 mg; 50–70 kg: 600 mg daily.',
    dosageForms: 'Capsules/Tablets: 150 mg, 300 mg, 450 mg, 600 mg; 4-drug FDC (HRZE).',
    contraindications: 'Severe hepatic dysfunction; acute hepatitis.',
    warnings: 'Warn patients that tears, sweat, saliva, and urine will turn reddish-orange.',
    mechanism: 'Binds beta subunit of bacterial DNA-dependent RNA polymerase, blocking RNA transcription.',
    relatedDisease: 'tuberculosis',
    source: 'data.gov.in / NTEP / NLEM',
    isIndiaMainApi: true,
  },
  {
    id: 'in-isoniazid',
    slug: 'isoniazid',
    name: 'Isoniazid (INH)',
    genericName: 'ISONIAZID',
    class: 'Nicotinic Acid Hydrazide / Mycolic Acid Inhibitor',
    category: 'Antibiotic',
    subCategory: 'Antitubercular',
    routes: ['Oral', 'IM'],
    nlemTier: 'Primary, Secondary, Tertiary (NLEM Category 6.2.4)',
    cdscoStatus: 'Schedule H; Core first-line NTEP medicine',
    nppaRegulated: true,
    desc: 'Cornerstone bactericidal antibiotic for tuberculosis. Inhibits mycolic acid synthesis in the mycobacterial cell wall.',
    dose: 'Adults: 5 mg/kg daily (typically 300 mg once daily). Paired with Pyridoxine (Vitamin B6) 10–25 mg daily to prevent neuropathy.',
    caution: 'Hepatotoxicity and peripheral neuropathy. Monitor liver transaminases in high-risk patients.',
    indications: 'Active tuberculosis (intensive and continuation phases); Latent TB Infection preventive therapy.',
    dosage: 'Standard adult dose: 300 mg orally once daily. Pediatric: 10 mg/kg daily.',
    dosageForms: 'Tablets: 100 mg, 300 mg; FDC tablets; Syrup: 50 mg / 5 mL.',
    contraindications: 'Previous isoniazid-induced acute hepatic injury or severe drug-induced hepatitis.',
    warnings: 'Discontinue if transaminases exceed 5x upper limit of normal or if jaundice develops.',
    mechanism: 'Activated by KatG; inhibits InhA enoyl-ACP reductase required for mycolic acid synthesis.',
    relatedDisease: 'tuberculosis',
    source: 'data.gov.in / NTEP / NLEM',
    isIndiaMainApi: true,
  },
  {
    id: 'in-pyrazinamide',
    slug: 'pyrazinamide',
    name: 'Pyrazinamide',
    genericName: 'PYRAZINAMIDE',
    class: 'Pyrazinoic Acid Prodrug / Sterilizing Antitubercular',
    category: 'Antibiotic',
    subCategory: 'Antitubercular',
    routes: ['Oral'],
    nlemTier: 'Primary, Secondary, Tertiary (NLEM Category 6.2.4)',
    cdscoStatus: 'Schedule H; Core Intensive-Phase NTEP Generic',
    nppaRegulated: true,
    desc: 'Sterilizing antitubercular agent active against semidormant bacilli inside acidic macrophage phagolysosomes. Key drug shortening TB therapy from 9 to 6 months.',
    dose: 'Adults: 25 mg/kg daily (1000–1500 mg once daily during 2-month intensive phase). Take with food.',
    caution: 'Hyperuricemia with non-gouty arthralgia; hepatotoxicity. Monitor serum uric acid and liver enzymes.',
    indications: 'Initial 2-month intensive phase of pulmonary and extrapulmonary tuberculosis.',
    dosage: 'Weight-banded: 35–49 kg: 1000 mg; 50–70 kg: 1500 mg; >70 kg: 2000 mg daily.',
    dosageForms: 'Tablets: 500 mg, 750 mg, 1000 mg; Part of 4-FDC tablets (HRZE).',
    contraindications: 'Severe hepatic impairment; acute gouty arthritis; porphyria.',
    warnings: 'Uric acid elevation is very common; mild arthralgia can be managed with paracetamol without stopping drug.',
    mechanism: 'Converted by mycobacterial pyrazinamidase into pyrazinoic acid, disrupting bacterial membrane potential.',
    relatedDisease: 'tuberculosis',
    source: 'data.gov.in / NTEP / NLEM',
    isIndiaMainApi: true,
  },
  {
    id: 'in-ethambutol',
    slug: 'ethambutol',
    name: 'Ethambutol Hydrochloride',
    genericName: 'ETHAMBUTOL HYDROCHLORIDE',
    class: 'Arabinofuranosyl Transferase Inhibitor',
    category: 'Antibiotic',
    subCategory: 'Antitubercular',
    routes: ['Oral'],
    nlemTier: 'Primary, Secondary, Tertiary (NLEM Category 6.2.4)',
    cdscoStatus: 'Schedule H; First-Line NTEP Companion Generic',
    nppaRegulated: true,
    desc: 'Bacteriostatic first-line companion antitubercular drug preventing the emergence of drug-resistant mutants.',
    dose: 'Adults: 15–20 mg/kg daily (800–1200 mg once daily) during intensive phase of TB therapy.',
    caution: 'Dose-dependent retrobulbar optic neuritis (decreased visual acuity, red-green color blindness). Test visual acuity at baseline.',
    indications: 'Intensive phase of tuberculosis therapy in combination with Rifampicin, Isoniazid, and Pyrazinamide.',
    dosage: 'Adults: 15 mg/kg once daily. Reduce dose in renal impairment (adjust frequency to thrice weekly).',
    dosageForms: 'Tablets: 400 mg, 600 mg, 800 mg.',
    contraindications: 'Pre-existing optic neuritis; children too young for reliable visual acuity testing.',
    warnings: 'Promptly discontinue if patient notices decreased vision or loss of color discrimination.',
    mechanism: 'Inhibits arabinosyl transferase III, blocking arabinogalactan synthesis in mycobacterial cell wall.',
    relatedDisease: 'tuberculosis',
    source: 'data.gov.in / NTEP / NLEM',
    isIndiaMainApi: true,
  },
  {
    id: 'in-bedaquiline',
    slug: 'bedaquiline',
    name: 'Bedaquiline Fumarate',
    genericName: 'BEDAQUILINE FUMARATE',
    class: 'Diarylquinoline / Mycobacterial ATP Synthase Inhibitor',
    category: 'Antibiotic',
    subCategory: 'MDR-TB Specialist Generic',
    routes: ['Oral'],
    nlemTier: 'Tertiary Medical Colleges & PMDT Centres (NLEM Category 6.2.4)',
    cdscoStatus: 'Approved Conditional Access for MDR/XDR-TB under NTEP',
    nppaRegulated: true,
    desc: 'Novel diarylquinoline antimycobacterial agent. Essential core of all-oral regimens for Multidrug-Resistant (MDR) and Rifampicin-Resistant (RR) Tuberculosis across India.',
    dose: '400 mg once daily for weeks 1–2, followed by 200 mg thrice weekly for weeks 3–24. Must be taken with food.',
    caution: 'QTcF interval prolongation. Perform baseline and monthly ECG; avoid with other strong QT-prolonging agents.',
    indications: 'Pulmonary Multidrug-Resistant (MDR-TB) and Pre-XDR-TB in adult and adolescent patients under NTEP.',
    dosage: 'Weeks 1–2: 400 mg once daily; Weeks 3–24: 200 mg on Mon, Wed, Fri (total 24-week course).',
    dosageForms: 'Tablets: 100 mg; Pediatric dispersible tablets: 20 mg.',
    contraindications: 'Severe cardiac arrhythmias; baseline QTcF > 450 ms.',
    warnings: 'Strictly monitored under NTEP Programmatic Management of Drug-Resistant TB (PMDT).',
    mechanism: 'Selectively binds to subunit c of mycobacterial ATP synthase, depleting intracellular ATP.',
    relatedDisease: 'tuberculosis',
    source: 'data.gov.in / NTEP / NLEM',
    isIndiaMainApi: true,
  },

  // ----------------------------------------------------
  // SECTION 3: BROAD-SPECTRUM ANTIMICROBIALS & ANTIBIOTICS
  // ----------------------------------------------------
  {
    id: 'in-amoxicillin',
    slug: 'amoxicillin',
    name: 'Amoxicillin',
    genericName: 'AMOXICILLIN TRIHYDRATE',
    class: 'Aminopenicillin Antibiotic',
    category: 'Antibiotic',
    subCategory: 'Broad-Spectrum Penicillin',
    routes: ['Oral'],
    nlemTier: 'Sub-Centre, Primary, Secondary, Tertiary (NLEM Category 6.2.1)',
    cdscoStatus: 'Universal Essential Antibiotic; First-Line for Pediatric Pneumonia & ENT',
    nppaRegulated: true,
    desc: 'Broad-spectrum beta-lactam aminopenicillin. Universal first-line oral antibiotic under WHO and India IMNCI guidelines for pediatric community-acquired pneumonia, otitis media, and strep throat.',
    dose: 'Adults: 500 mg every 8 hours or 875 mg every 12 hours. Children: 40–90 mg/kg/day in 2–3 divided doses.',
    caution: 'Contraindicated in penicillin allergy (anaphylaxis risk). Non-allergic maculopapular rash occurs if given in infectious mononucleosis (EBV).',
    indications: 'Acute otitis media, sinusitis, community-acquired pneumonia, acute exacerbation of chronic bronchitis, uncomplicated UTI, H. pylori eradication.',
    dosage: 'Tablets/Capsules: 250 mg, 500 mg; Dispersible tablets: 125 mg, 250 mg; Oral suspension: 125 mg/5 mL, 250 mg/5 mL.',
    dosageForms: 'Capsules: 250 mg, 500 mg; Dispersible tablets; Suspension.',
    contraindications: 'True immediate hypersensitivity (anaphylaxis, urticaria) to penicillins or beta-lactams.',
    warnings: 'In children with fast breathing pneumonia under IMNCI, 5 days of oral amoxicillin is standard.',
    mechanism: 'Inhibits bacterial cell wall synthesis by binding to penicillin-binding proteins (PBPs), causing bacterial lysis.',
    relatedDisease: 'respiratory-infections',
    source: 'data.gov.in / CDSCO / NLEM',
    isIndiaMainApi: true,
  },
  {
    id: 'in-amoxicillin-clavulanate',
    slug: 'amoxicillin-and-clavulanate-potassium',
    name: 'Amoxicillin + Clavulanic Acid (Co-Amoxiclav)',
    genericName: 'AMOXICILLIN AND CLAVULANATE POTASSIUM',
    class: 'Aminopenicillin + Beta-Lactamase Inhibitor',
    category: 'Antibiotic',
    subCategory: 'Broad-Spectrum Antibiotic',
    routes: ['Oral', 'IV'],
    nlemTier: 'Primary, Secondary, Tertiary (NLEM Category 6.2.1)',
    cdscoStatus: 'Schedule H1 Generic Antibiotic (Prescription Monitoring Regulated)',
    nppaRegulated: true,
    desc: 'Potent bactericidal combination. Clavulanate protects amoxicillin from beta-lactamase degradation.',
    dose: 'Adults: 625 mg (500/125 mg) orally every 8 hours or 1000 mg (875/125 mg) every 12 hours. Severe infections: 1.2 g IV every 8 hours.',
    caution: 'Cholestatic jaundice / acute hepatitis with clavulanate. Schedule H1 prescription register required.',
    indications: 'Community-acquired pneumonia, acute sinusitis, dog/human bites, skin infections, complicated UTI.',
    dosage: 'Oral tablets: 375 mg, 625 mg, 1000 mg; Pediatric dry syrup; Injection: 600 mg, 1.2 g.',
    dosageForms: 'Tablets: 375 mg, 625 mg, 1000 mg; Dry syrup; IV injection.',
    contraindications: 'Penicillin allergy; prior amoxicillin/clavulanate-induced jaundice.',
    warnings: 'Schedule H1 antimicrobial: Dispensing without prescription strictly prohibited.',
    mechanism: 'Amoxicillin binds PBPs; clavulanic acid irreversibly inactivates beta-lactamase enzymes.',
    relatedDisease: 'respiratory-infections',
    source: 'data.gov.in / CDSCO / NLEM',
    isIndiaMainApi: true,
  },
  {
    id: 'in-ceftriaxone',
    slug: 'ceftriaxone',
    name: 'Ceftriaxone Sodium',
    genericName: 'CEFTRIAXONE SODIUM',
    class: 'Third-Generation Cephalosporin',
    category: 'Antibiotic',
    subCategory: 'Broad-Spectrum Parenteral',
    routes: ['IV', 'IM'],
    nlemTier: 'Community Health Centre (CHC), District Hospital (NLEM Category 6.2.1)',
    cdscoStatus: 'Schedule H1; Essential Inpatient Hospital Antimicrobial',
    nppaRegulated: true,
    desc: 'Third-generation cephalosporin with excellent cerebrospinal fluid penetration. Essential for inpatient management of bacterial meningitis, severe enteric fever (typhoid), and sepsis.',
    dose: 'Adults: 1 g to 2 g IV/IM once daily. Bacterial meningitis: 2 g IV every 12 hours (total 4 g/day).',
    caution: 'Biliary sludging with prolonged high doses. In neonates: never co-administer with IV calcium.',
    indications: 'Severe bacterial meningitis, empirical sepsis therapy, typhoid enteric fever, pyelonephritis.',
    dosage: '1 g or 2 g IV over 30 minutes in normal saline, or deep IM injection with 1% lidocaine.',
    dosageForms: 'Vials for injection: 250 mg, 500 mg, 1 g, 2 g.',
    contraindications: 'Severe cephalosporin anaphylaxis; hyperbilirubinemic neonates.',
    warnings: 'Schedule H1 antimicrobial: Restrict to hospital inpatient care.',
    mechanism: 'Interferes with peptidoglycan synthesis by binding to PBPs 1a, 1b, 2, 3.',
    relatedDisease: 'typhoid',
    source: 'data.gov.in / CDSCO / NLEM',
    isIndiaMainApi: true,
  },
  {
    id: 'in-cefixime',
    slug: 'cefixime',
    name: 'Cefixime',
    genericName: 'CEFIXIME TRIHYDRATE',
    class: 'Third-Generation Oral Cephalosporin',
    category: 'Antibiotic',
    subCategory: 'Oral Cephalosporin',
    routes: ['Oral'],
    nlemTier: 'Primary, Secondary, Tertiary (NLEM Category 6.2.1)',
    cdscoStatus: 'Schedule H1; First-Line Oral Agent for Typhoid Step-Down & UTI',
    nppaRegulated: true,
    desc: 'Potent oral third-generation cephalosporin with stability against beta-lactamases. Widely used for oral step-down treatment of enteric fever (typhoid), pyelonephritis, and uncomplicated gonorrhea.',
    dose: 'Adults: 200–400 mg once daily or divided into 200 mg every 12 hours for 7–14 days. Children: 8 mg/kg/day in 1–2 doses.',
    caution: 'Clostridium difficile-associated diarrhea; mild loose stools common. Adjust dose in renal impairment (CrCl < 20 mL/min).',
    indications: 'Enteric fever (typhoid), acute bacterial sinusitis, acute exacerbations of chronic bronchitis, uncomplicated cystitis, gonorrhea.',
    dosage: 'Tablets: 100 mg, 200 mg, 400 mg; Dispersible tablets: 50 mg, 100 mg; Dry syrup: 50 mg / 5 mL, 100 mg / 5 mL.',
    dosageForms: 'Tablets: 200 mg, 400 mg; Dispersible tablets; Oral suspension.',
    contraindications: 'Immediate hypersensitivity to cephalosporins or severe penicillin anaphylaxis.',
    warnings: 'Complete full prescribed course to prevent emergence of resistant S. Typhi strains.',
    mechanism: 'Binds to and inactivates penicillin-binding proteins in bacterial cell wall, preventing peptidoglycan cross-linking.',
    relatedDisease: 'typhoid',
    source: 'data.gov.in / CDSCO / NLEM',
    isIndiaMainApi: true,
  },
  {
    id: 'in-ciprofloxacin',
    slug: 'ciprofloxacin',
    name: 'Ciprofloxacin',
    genericName: 'CIPROFLOXACIN HYDROCHLORIDE',
    class: 'Fluoroquinolone Antibacterial',
    category: 'Antibiotic',
    subCategory: 'Fluoroquinolone',
    routes: ['Oral', 'IV', 'Ophthalmic'],
    nlemTier: 'Primary, Secondary, Tertiary (NLEM Category 6.2.2)',
    cdscoStatus: 'Schedule H1; Essential Antimicrobial for Gram-Negative Infections',
    nppaRegulated: true,
    desc: 'Potent bactericidal fluoroquinolone with outstanding tissue penetration. Effective against Pseudomonas aeruginosa, enteric gram-negative bacilli, and intracellular pathogens.',
    dose: 'Adults: 500–750 mg orally every 12 hours, or 400 mg IV every 8–12 hours. Uncomplicated cystitis: 250–500 mg twice daily for 3 days.',
    caution: 'Tendinitis and tendon rupture (Achilles tendon); QT prolongation; peripheral neuropathy; exacerbates myasthenia gravis.',
    indications: 'Complicated urinary tract infections, pyelonephritis, infectious bacterial diarrhea, intra-abdominal infections (with metronidazole), anthrax post-exposure.',
    dosage: 'Tablets: 250 mg, 500 mg, 750 mg; IV infusion: 200 mg/100 mL, 400 mg/200 mL; Eye/Ear drops: 0.3%.',
    dosageForms: 'Tablets: 250 mg, 500 mg, 750 mg; IV solution; Eye/ear drops.',
    contraindications: 'Concurrent administration with tizanidine; prior tendon disorders related to fluoroquinolones.',
    warnings: 'Avoid in children unless specific indication (anthrax, cystic fibrosis pseudomonas) due to arthropathy risk.',
    mechanism: 'Inhibits bacterial DNA gyrase (topoisomerase II) and topoisomerase IV, blocking bacterial DNA replication.',
    relatedDisease: 'acute-diarrheal-disease',
    source: 'data.gov.in / CDSCO / NLEM',
    isIndiaMainApi: true,
  },
  {
    id: 'in-azithromycin',
    slug: 'azithromycin',
    name: 'Azithromycin',
    genericName: 'AZITHROMYCIN DIHYDRATE',
    class: 'Macrolide / Azalide Antibiotic',
    category: 'Antibiotic',
    subCategory: 'Atypical & Enteric Antimicrobial',
    routes: ['Oral', 'IV'],
    nlemTier: 'Primary, Secondary, Tertiary (NLEM Category 6.2.2)',
    cdscoStatus: 'Schedule H1; Essential Antibiotic for Atypical Pneumonia & Typhoid',
    nppaRegulated: true,
    desc: 'Azalide subclass macrolide with extended tissue half-life (~68 hours). First-line oral agent for atypical pneumonia and typhoid fever.',
    dose: 'Respiratory infections: 500 mg once daily for 3 days. Typhoid fever: 1000 mg (20 mg/kg) once daily for 7 days.',
    caution: 'QT interval prolongation. Schedule H1 prescription antibiotic.',
    indications: 'Community-acquired atypical pneumonia, typhoid fever, chlamydial urethritis, pertussis, trachoma.',
    dosage: 'Tablets: 250 mg, 500 mg; Oral suspension: 100 mg/5 mL, 200 mg/5 mL; Injection vial: 500 mg.',
    dosageForms: 'Tablets: 250 mg, 500 mg; Suspension; Injection vial.',
    contraindications: 'Prior cholestatic jaundice/hepatic dysfunction with azithromycin; macrolide allergy.',
    warnings: 'Avoid overuse in viral URIs to preserve efficacy.',
    mechanism: 'Binds to 50S ribosomal subunit, inhibiting transpeptidation and protein synthesis.',
    relatedDisease: 'typhoid',
    source: 'data.gov.in / CDSCO / NLEM',
    isIndiaMainApi: true,
  },
  {
    id: 'in-doxycycline',
    slug: 'doxycycline',
    name: 'Doxycycline',
    genericName: 'DOXYCYCLINE HYCLATE',
    class: 'Tetracycline Antibiotic',
    category: 'Antibiotic',
    subCategory: 'Broad-Spectrum Tetracycline',
    routes: ['Oral', 'IV'],
    nlemTier: 'Primary, Secondary, Tertiary (NLEM Category 6.2.3)',
    cdscoStatus: 'Schedule H; Drug of Choice for Scrub Typhus & Leptospirosis in India',
    nppaRegulated: true,
    desc: 'Broad-spectrum tetracycline derivative. The national drug of choice under IDSP for seasonal outbreaks of Scrub Typhus, Leptospirosis, Rickettsial fevers, and Cholera.',
    dose: 'Adults: 100 mg orally every 12 hours for 7–14 days. Scrub Typhus / Leptospirosis: 100 mg twice daily for 7 days. Take with a full glass of water sitting upright.',
    caution: 'Severe pill-induced esophageal ulceration if taken without water or lying down. Photosensitivity; permanent tooth discoloration in children <8 years.',
    indications: 'Scrub typhus (Orientia tsutsugamushi), leptospirosis, cholera, Lyme disease, acne vulgaris, malaria chemoprophylaxis.',
    dosage: 'Capsules/Tablets: 50 mg, 100 mg; Powder for injection: 100 mg.',
    dosageForms: 'Capsules: 100 mg; Tablets: 100 mg; IV vial.',
    contraindications: 'Pregnancy (2nd and 3rd trimesters); severe hepatic dysfunction; children under 8 years (except for life-threatening rickettsial infections).',
    warnings: 'Remain upright for at least 30 minutes after taking capsule to prevent esophageal impaction.',
    mechanism: 'Reversibly binds to 30S ribosomal subunit, preventing access of aminoacyl-tRNA to acceptor site.',
    relatedDisease: 'acute-diarrheal-disease',
    source: 'data.gov.in / IDSP / NLEM',
    isIndiaMainApi: true,
  },
  {
    id: 'in-metronidazole',
    slug: 'metronidazole',
    name: 'Metronidazole',
    genericName: 'METRONIDAZOLE',
    class: 'Nitroimidazole Antiprotozoal & Antibacterial',
    category: 'Antibiotic',
    subCategory: 'Anaerobic & Antiprotozoal',
    routes: ['Oral', 'IV', 'Topical'],
    nlemTier: 'Sub-Centre, Primary, Secondary, Tertiary (NLEM Category 6.2.2)',
    cdscoStatus: 'Universal Essential Antiprotozoal Generic',
    nppaRegulated: true,
    desc: 'Synthetic nitroimidazole with potent activity against anaerobic bacteria (Bacteroides, Clostridium) and protozoa (Entamoeba histolytica, Giardia lamblia, Trichomonas vaginalis).',
    dose: 'Amebiasis: 400–800 mg orally every 8 hours for 7–10 days. Anaerobic infections: 500 mg IV every 8 hours. Giardiasis: 400 mg thrice daily for 5 days.',
    caution: 'Disulfiram-like reaction with alcohol (severe flushing, tachycardia, vomiting). Metallic taste in mouth is common. Peripheral neuropathy with prolonged use.',
    indications: 'Amebic liver abscess, amebic dysentery, giardiasis, trichomoniasis, intra-abdominal anaerobic infections, bacterial vaginosis, surgical prophylaxis.',
    dosage: 'Tablets: 200 mg, 400 mg; Suspension: 200 mg / 5 mL; IV infusion: 500 mg / 100 mL.',
    dosageForms: 'Tablets: 200 mg, 400 mg; Suspension; IV infusion bottle: 500 mg.',
    contraindications: 'First trimester of pregnancy; severe hypersensitivity to nitroimidazoles; concurrent alcohol consumption.',
    warnings: 'Avoid all alcohol during treatment and for at least 48 hours following the final dose.',
    mechanism: 'Reduced by microbial pyruvate:ferredoxin oxidoreductase to toxic nitro radical intermediates that break DNA strands.',
    relatedDisease: 'acute-diarrheal-disease',
    source: 'data.gov.in / NLEM',
    isIndiaMainApi: true,
  },
  {
    id: 'in-gentamicin',
    slug: 'gentamicin',
    name: 'Gentamicin Sulphate',
    genericName: 'GENTAMICIN SULPHATE',
    class: 'Aminoglycoside Antibiotic',
    category: 'Antibiotic',
    subCategory: 'Aminoglycoside',
    routes: ['IV', 'IM', 'Ophthalmic'],
    nlemTier: 'Primary Health Centre, CHC, District Hospital (NLEM Category 6.2.2)',
    cdscoStatus: 'Schedule H; Essential Hospital Sepsis & Neonatal Generic',
    nppaRegulated: true,
    desc: 'Bactericidal aminoglycoside active against severe gram-negative aerobic bacilli. Standard component of neonatal sepsis and infective endocarditis regimens.',
    dose: 'Adults: 5–7 mg/kg IV once daily (extended-interval dosing), or 1.5 mg/kg every 8 hours. Adjust strictly based on renal function.',
    caution: 'Nephrotoxicity (acute tubular necrosis) and irreversible ototoxicity (vestibular and cochlear). Monitor serum creatinine and trough levels.',
    indications: 'Severe gram-negative sepsis, neonatal sepsis (paired with ampicillin), infective endocarditis synergy, complicated pyelonephritis.',
    dosage: 'Injection: 20 mg/2 mL, 80 mg/2 mL vials; Eye/Ear drops: 0.3%.',
    dosageForms: 'Vials for IV/IM injection: 80 mg/2 mL; Eye/ear drops.',
    contraindications: 'Hypersensitivity to aminoglycosides; pre-existing severe renal failure; myasthenia gravis.',
    warnings: 'Maintain adequate hydration to minimize nephrotoxicity; avoid concurrent loop diuretics.',
    mechanism: 'Irreversibly binds 30S ribosomal subunit, causing misreading of genetic code and disrupting cell membrane integrity.',
    relatedDisease: 'respiratory-infections',
    source: 'data.gov.in / NLEM',
    isIndiaMainApi: true,
  },
  {
    id: 'in-piperacillin-tazobactam',
    slug: 'piperacillin-and-tazobactam',
    name: 'Piperacillin + Tazobactam',
    genericName: 'PIPERACILLIN AND TAZOBACTAM',
    class: 'Antipseudomonal Penicillin + Beta-Lactamase Inhibitor',
    category: 'Antibiotic',
    subCategory: 'Broad-Spectrum Hospital Parenteral',
    routes: ['IV'],
    nlemTier: 'Community Health Centre, District Hospital, Medical College (NLEM Category 6.2.1)',
    cdscoStatus: 'Schedule H1; Essential Inpatient Critical Care Antibiotic',
    nppaRegulated: true,
    desc: 'Extended-spectrum antipseudomonal penicillin combination with tazobactam. Foundational therapy for hospital-acquired pneumonia, intra-abdominal sepsis, and febrile neutropenia.',
    dose: 'Adults: 4.5 g IV infusion every 6 hours (administered over 30 minutes, or extended infusion over 3–4 hours for Pseudomonas).',
    caution: 'Increased acute kidney injury when combined with vancomycin. Hypokalemia from sodium load. Schedule H1 recording required.',
    indications: 'Hospital-acquired / ventilator-associated pneumonia, severe intra-abdominal peritonitis, complicated skin infections, neutropenic fever.',
    dosage: 'Vials for injection: 2.25 g (2g/0.25g), 4.5 g (4g/0.5g).',
    dosageForms: 'Lyophilized powder for IV infusion: 2.25 g, 4.5 g.',
    contraindications: 'History of allergic reactions to penicillins, cephalosporins, or beta-lactamase inhibitors.',
    warnings: 'Schedule H1 hospital antibiotic: Reserve for severe multidrug-resistant inpatient infections.',
    mechanism: 'Piperacillin inhibits bacterial cell wall synthesis; tazobactam inhibits class A and C beta-lactamases.',
    relatedDisease: 'respiratory-infections',
    source: 'data.gov.in / CDSCO / NLEM',
    isIndiaMainApi: true,
  },

  // ----------------------------------------------------
  // SECTION 4: ANALGESICS, NSAIDS & ANTIPYRETICS
  // ----------------------------------------------------
  {
    id: 'in-paracetamol',
    slug: 'paracetamol',
    name: 'Paracetamol (Acetaminophen)',
    genericName: 'PARACETAMOL',
    class: 'Centrally-Acting Analgesic & Antipyretic',
    category: 'Analgesic',
    subCategory: 'First-Line Antipyretic',
    routes: ['Oral', 'IV', 'Rectal'],
    nlemTier: 'Sub-Centre, Primary, Secondary, Tertiary (NLEM Category 2.1)',
    cdscoStatus: 'Universal Over-The-Counter Essential; National Dengue First-Line',
    nppaRegulated: true,
    desc: 'Universal first-line antipyretic and analgesic. The ONLY recommended antipyretic for Dengue and Chikungunya fevers under NVBDCP and IDSP guidelines.',
    dose: 'Adults: 500–1000 mg every 4–6 hours (max 4000 mg/day). Children: 10–15 mg/kg per dose every 4–6 hours.',
    caution: 'Hepatotoxicity in overdose (>10 g). Antidote is N-Acetylcysteine. Strictly avoid NSAIDs in Dengue.',
    indications: 'Mild to moderate pain, pyrexia, primary fever management in Dengue, Malaria, Typhoid, viral fevers.',
    dosage: 'Tablets: 500 mg, 650 mg; Syrup: 120 mg/5 mL, 250 mg/5 mL; Drops: 100 mg/mL; IV infusion: 10 mg/mL.',
    dosageForms: 'Tablets: 500 mg, 650 mg; Drops; Oral suspension; IV infusion.',
    contraindications: 'Severe acute hepatic failure or decompensated chronic liver disease.',
    warnings: 'In Dengue fever, maintain dosing interval ≥ 4–6 hours; do not exceed 60 mg/kg/day in children.',
    mechanism: 'Inhibits central cyclooxygenase (COX-1/COX-2/COX-3) in the brain, reducing hypothalamic prostaglandin E2.',
    relatedDisease: 'dengue',
    source: 'data.gov.in / CBHI / IDSP / NLEM',
    isIndiaMainApi: true,
  },
  {
    id: 'in-ibuprofen',
    slug: 'ibuprofen',
    name: 'Ibuprofen',
    genericName: 'IBUPROFEN',
    class: 'Propionic Acid Non-Steroidal Anti-Inflammatory Drug (NSAID)',
    category: 'Analgesic',
    subCategory: 'Non-Steroidal Anti-Inflammatory',
    routes: ['Oral'],
    nlemTier: 'Sub-Centre, Primary, Secondary, Tertiary (NLEM Category 2.1)',
    cdscoStatus: 'Schedule H / Essential Anti-Inflammatory Analgesic',
    nppaRegulated: true,
    desc: 'Non-selective reversible COX inhibitor. Standard anti-inflammatory agent for musculoskeletal pain, inflammatory arthritis, dental pain, and dysmenorrhea.',
    dose: 'Adults: 200–400 mg orally every 4–6 hours with meals (max 2400 mg/day). Anti-inflammatory dose: 600–800 mg thrice daily.',
    caution: 'Gastric ulceration and bleeding; renal impairment; worsens heart failure. STRICTLY CONTRAINDICATED in Dengue fever (causes severe hemorrhage).',
    indications: 'Osteoarthritis, rheumatoid arthritis, dysmenorrhea, musculoskeletal pain, tension headache, migraine.',
    dosage: 'Tablets: 200 mg, 400 mg, 600 mg; Oral suspension: 100 mg / 5 mL.',
    dosageForms: 'Film-coated tablets: 200 mg, 400 mg, 600 mg; Suspension.',
    contraindications: 'Active peptic ulcer disease; severe renal or heart failure; third trimester of pregnancy; suspected or confirmed Dengue fever.',
    warnings: 'Never administer to febrile patients during monsoon Dengue outbreaks without confirming absence of thrombocytopenia.',
    mechanism: 'Reversibly inhibits cyclooxygenase-1 (COX-1) and cyclooxygenase-2 (COX-2), decreasing peripheral pro-inflammatory prostaglandins.',
    relatedDisease: 'dengue',
    source: 'data.gov.in / NLEM',
    isIndiaMainApi: true,
  },
  {
    id: 'in-diclofenac',
    slug: 'diclofenac',
    name: 'Diclofenac Sodium',
    genericName: 'DICLOFENAC SODIUM',
    class: 'Phenylacetic Acid NSAID',
    category: 'Analgesic',
    subCategory: 'Anti-Inflammatory Analgesic',
    routes: ['Oral', 'IM', 'IV', 'Topical'],
    nlemTier: 'Primary Health Centre, CHC, District Hospital (NLEM Category 2.1)',
    cdscoStatus: 'Schedule H; Essential Acute Musculoskeletal & Post-Op Analgesic',
    nppaRegulated: true,
    desc: 'Potent non-steroidal anti-inflammatory drug with preferential COX-2 inhibition. Highly effective for acute renal colic, post-operative pain, and inflammatory joint flare-ups.',
    dose: 'Adults: 50 mg orally 2–3 times daily, or 75 mg once or twice daily (max 150 mg/day). Acute renal colic: 75 mg deep IM injection.',
    caution: 'Increased cardiovascular thrombotic risk (similar to selective COX-2 inhibitors); GI ulceration. Avoid in patients with established ischemic heart disease.',
    indications: 'Acute renal colic, acute gouty arthritis, post-operative surgical pain, osteoarthritis, ankylosing spondylitis.',
    dosage: 'Tablets (enteric-coated): 50 mg, 75 mg; Ampoules for injection: 75 mg / 1 mL, 75 mg / 3 mL; Topical gel: 1%.',
    dosageForms: 'Enteric-coated tablets; SR tablets: 100 mg; Injection ampoules; Gel.',
    contraindications: 'Congestive heart failure (NYHA II-IV); established coronary artery disease; active GI bleeding; Dengue fever.',
    warnings: 'Use the lowest effective dose for the shortest duration necessary.',
    mechanism: 'Inhibits prostaglandin synthesis via COX-1 and COX-2; also reduces intracellular arachidonic acid.',
    relatedDisease: 'dengue',
    source: 'data.gov.in / NLEM',
    isIndiaMainApi: true,
  },
  {
    id: 'in-tramadol',
    slug: 'tramadol',
    name: 'Tramadol Hydrochloride',
    genericName: 'TRAMADOL HYDROCHLORIDE',
    class: 'Centrally-Acting Synthetic Opioid Analgesic',
    category: 'Analgesic',
    subCategory: 'Moderate Opioid Analgesic',
    routes: ['Oral', 'IV', 'IM'],
    nlemTier: 'Primary, Secondary, Tertiary (NLEM Category 2.2)',
    cdscoStatus: 'Schedule H1 / NDPS Regulated Central Analgesic',
    nppaRegulated: true,
    desc: 'Dual-action centrally acting analgesic: weak mu-opioid receptor agonist combined with norepinephrine and serotonin reuptake inhibition.',
    dose: 'Adults: 50–100 mg orally or IV/IM every 4–6 hours as needed (max 400 mg/day). Elderly (>75y): max 300 mg/day.',
    caution: 'Lowers seizure threshold (risk of convulsions, especially with antidepressants); nausea and dizziness; risk of serotonin syndrome with SSRIs/SNRIs.',
    indications: 'Moderate to moderately severe acute post-operative pain, fracture pain, chronic cancer pain (Step 2 of WHO analgesic ladder).',
    dosage: 'Capsules/Tablets: 50 mg, 100 mg; SR tablets: 100 mg, 200 mg; Injection: 50 mg/mL (1 mL and 2 mL ampoules).',
    dosageForms: 'Immediate-release tablets; Sustained-release tablets; Ampoules for injection.',
    contraindications: 'Uncontrolled epilepsy; acute intoxication with alcohol, hypnotics, or centrally acting agents; concurrent MAOIs.',
    warnings: 'Administer with caution in patients with history of seizures or head trauma.',
    mechanism: 'Binds to mu-opioid receptors and inhibits reuptake of norepinephrine and serotonin in ascending pain pathways.',
    source: 'data.gov.in / NLEM',
    isIndiaMainApi: true,
  },
  {
    id: 'in-morphine',
    slug: 'morphine',
    name: 'Morphine Sulphate',
    genericName: 'MORPHINE SULPHATE',
    class: 'Opioid Agonist / Pure Mu-Receptor Agonist',
    category: 'Analgesic',
    subCategory: 'Strong Opioid Analgesic',
    routes: ['Oral', 'IV', 'IM', 'SC'],
    nlemTier: 'District Hospital, Medical College, Palliative Care (NLEM Category 2.2)',
    cdscoStatus: 'Recognized Medical Institution (RMI) NDPS Essential Narcotic Drug',
    nppaRegulated: true,
    desc: 'The gold standard opioid analgesic for severe pain management under WHO cancer pain guidelines and palliative care in India.',
    dose: 'Acute severe pain: 5–10 mg IV/IM every 3–4 hours titrated to pain score. Cancer pain: start with oral immediate release 5–10 mg every 4 hours, titrate.',
    caution: 'Respiratory depression (antidote: Naloxone); constipation (always co-prescribe laxative); nausea; sedation; physical dependence.',
    indications: 'Severe cancer pain, acute myocardial infarction (cardiac chest pain), acute pulmonary edema, severe polytrauma, palliative end-of-life care.',
    dosage: 'Oral tablets: 10 mg, 20 mg, 30 mg; Injection ampoules: 10 mg/mL, 15 mg/mL.',
    dosageForms: 'Immediate-release tablets; Sustained-release tablets; Injection ampoules.',
    contraindications: 'Severe respiratory depression; acute bronchial asthma; paralytic ileus; head injury with elevated intracranial pressure.',
    warnings: 'Strict NDPS regulatory compliance required; maintain respiratory monitoring during parenteral titration.',
    mechanism: 'Acts as a full agonist at central mu-opioid receptors, opening K+ channels and inhibiting Ca2+ channels to block nociceptive transmission.',
    source: 'data.gov.in / NLEM',
    isIndiaMainApi: true,
  },

  // ----------------------------------------------------
  // SECTION 5: CARDIOLOGY & CARDIOVASCULAR MEDICINES
  // ----------------------------------------------------
  {
    id: 'in-amlodipine',
    slug: 'amlodipine',
    name: 'Amlodipine Besylate',
    genericName: 'AMLODIPINE BESYLATE',
    class: 'Dihydropyridine Calcium Channel Blocker (CCB)',
    category: 'Cardiology',
    subCategory: 'Antihypertensive',
    routes: ['Oral'],
    nlemTier: 'Primary Health Centre (PHC), CHC, District Hospital (NLEM Category 12.3)',
    cdscoStatus: 'Schedule H; Step-1 Antihypertensive in India Hypertension Control Initiative (IHCI)',
    nppaRegulated: true,
    desc: 'Long-acting dihydropyridine calcium channel blocker. The foundational Step-1 medication in the India Hypertension Control Initiative (IHCI).',
    dose: 'Initial: 5 mg orally once daily; titrate after 2–4 weeks to 10 mg once daily if BP target (<140/90 mmHg) is not achieved.',
    caution: 'Peripheral ankle edema. Combine with ACE inhibitor/ARB to mitigate edema.',
    indications: 'Essential hypertension, chronic stable angina pectoris, vasospastic angina.',
    dosage: '5 mg to 10 mg orally once daily.',
    dosageForms: 'Tablets: 2.5 mg, 5 mg, 10 mg; Fixed-dose combinations.',
    contraindications: 'Severe hypotension (systolic BP < 90 mmHg); cardiogenic shock; severe aortic stenosis.',
    warnings: 'Peripheral ankle swelling is common at 10 mg dose; reassure patients it is not heart failure.',
    mechanism: 'Inhibits L-type calcium channels on vascular smooth muscle, causing peripheral vasodilation.',
    relatedDisease: 'hypertension',
    source: 'data.gov.in / IHCI / NLEM',
    isIndiaMainApi: true,
  },
  {
    id: 'in-telmisartan',
    slug: 'telmisartan',
    name: 'Telmisartan',
    genericName: 'TELMISARTAN',
    class: 'Angiotensin II Receptor Blocker (ARB)',
    category: 'Cardiology',
    subCategory: 'Antihypertensive & Renoprotective',
    routes: ['Oral'],
    nlemTier: 'Primary, Secondary, Tertiary (NLEM Category 12.3)',
    cdscoStatus: 'Schedule H; Core Step-2 Antihypertensive in National IHCI Protocol',
    nppaRegulated: true,
    desc: 'Longest-acting Angiotensin II Receptor Blocker (elimination half-life ~24 hours). Excellent organ protection for diabetic nephropathy.',
    dose: 'Standard dose: 40 mg orally once daily. May increase to 80 mg once daily if BP remains uncontrolled.',
    caution: 'Risk of hyperkalemia. Strictly contraindicated in pregnancy (fetotoxic).',
    indications: 'Essential hypertension, cardiovascular prevention, diabetic nephropathy with albuminuria.',
    dosage: '20 mg, 40 mg, or 80 mg orally once daily.',
    dosageForms: 'Tablets: 20 mg, 40 mg, 80 mg; Fixed-dose combinations.',
    contraindications: 'Pregnancy (2nd and 3rd trimesters); biliary obstructive disorders.',
    warnings: 'Monitor serum potassium and creatinine within 2–4 weeks of initiating therapy.',
    mechanism: 'Blocks AT1 subtype of angiotensin II receptor, preventing vasoconstriction and aldosterone release.',
    relatedDisease: 'hypertension',
    source: 'data.gov.in / IHCI / NLEM',
    isIndiaMainApi: true,
  },
  {
    id: 'in-losartan',
    slug: 'losartan',
    name: 'Losartan Potassium',
    genericName: 'LOSARTAN POTASSIUM',
    class: 'Angiotensin II Receptor Blocker (ARB)',
    category: 'Cardiology',
    subCategory: 'Antihypertensive & Renoprotective',
    routes: ['Oral'],
    nlemTier: 'Primary, Secondary, Tertiary (NLEM Category 12.3)',
    cdscoStatus: 'Schedule H; First-Line Antihypertensive Generic',
    nppaRegulated: true,
    desc: 'Prototypic oral Angiotensin II Receptor Blocker. Provides effective blood pressure reduction and slows diabetic nephropathy progression in patients with type 2 diabetes.',
    dose: 'Initial: 50 mg orally once daily; titrate to 100 mg once daily if needed. Patients on diuretics: start at 25 mg.',
    caution: 'Hyperkalemia; acute kidney injury in bilateral renal artery stenosis. Contraindicated in pregnancy.',
    indications: 'Hypertension, diabetic nephropathy with elevated serum creatinine, stroke risk reduction in hypertensive patients with LVH.',
    dosage: 'Tablets: 25 mg, 50 mg, 100 mg; Combination with Hydrochlorothiazide (50/12.5 mg).',
    dosageForms: 'Tablets: 25 mg, 50 mg, 100 mg.',
    contraindications: 'Pregnancy (all trimesters); concurrent use with aliskiren in patients with diabetes.',
    warnings: 'Monitor potassium and renal function periodically.',
    mechanism: 'Competitively blocks the AT1 receptor, inhibiting angiotensin II-induced vasoconstriction and aldosterone secretion.',
    relatedDisease: 'hypertension',
    source: 'data.gov.in / NLEM',
    isIndiaMainApi: true,
  },
  {
    id: 'in-enalapril',
    slug: 'enalapril',
    name: 'Enalapril Maleate',
    genericName: 'ENALAPRIL MALEATE',
    class: 'Angiotensin-Converting Enzyme (ACE) Inhibitor',
    category: 'Cardiology',
    subCategory: 'Heart Failure & Antihypertensive',
    routes: ['Oral'],
    nlemTier: 'Primary, Secondary, Tertiary (NLEM Category 12.3)',
    cdscoStatus: 'Schedule H; Essential Mortality-Reducing Drug in Heart Failure',
    nppaRegulated: true,
    desc: 'Prodrug converted to enalaprilat. Proved mortality-reducing foundation in heart failure with reduced ejection fraction (HFrEF) and hypertension.',
    dose: 'Hypertension: start 5 mg daily, titrate to 10–40 mg daily. Heart failure: start 2.5 mg twice daily, titrate to target 10–20 mg twice daily.',
    caution: 'Dry persistent cough (bradykinin accumulation, occurs in ~10–15% of patients); angioedema; hyperkalemia. Contraindicated in pregnancy.',
    indications: 'Heart failure with reduced ejection fraction, asymptomatic left ventricular dysfunction, essential hypertension.',
    dosage: 'Tablets: 2.5 mg, 5 mg, 10 mg, 20 mg.',
    dosageForms: 'Tablets: 2.5 mg, 5 mg, 10 mg, 20 mg.',
    contraindications: 'History of ACE inhibitor-induced angioedema; hereditary angioedema; pregnancy.',
    warnings: 'Switch to an ARB (Telmisartan/Losartan) if intolerable dry cough develops.',
    mechanism: 'Inhibits ACE, blocking conversion of angiotensin I to angiotensin II, decreasing afterload and aldosterone.',
    relatedDisease: 'hypertension',
    source: 'data.gov.in / NLEM',
    isIndiaMainApi: true,
  },
  {
    id: 'in-atorvastatin',
    slug: 'atorvastatin',
    name: 'Atorvastatin Calcium',
    genericName: 'ATORVASTATIN CALCIUM',
    class: 'HMG-CoA Reductase Inhibitor (Statin)',
    category: 'Cardiology',
    subCategory: 'Lipid-Lowering / Anti-atherosclerotic',
    routes: ['Oral'],
    nlemTier: 'Primary, Secondary, Tertiary (NLEM Category 12.6)',
    cdscoStatus: 'Schedule H; High-Yield Cardiovascular Prevention Generic',
    nppaRegulated: true,
    desc: 'Synthetic competitive inhibitor of HMG-CoA reductase. Significantly lowers LDL-C, triglycerides, and stabilizes atherosclerotic plaques.',
    dose: 'Initial: 10–20 mg once daily. High-intensity statin therapy for Acute Coronary Syndrome: 40–80 mg once daily.',
    caution: 'Myalgia and rare rhabdomyolysis. Monitor baseline liver enzymes; caution with strong CYP3A4 inhibitors.',
    indications: 'Primary hypercholesterolemia, secondary prevention of myocardial infarction, ischemic stroke.',
    dosage: '10 mg to 80 mg orally once daily.',
    dosageForms: 'Tablets: 10 mg, 20 mg, 40 mg, 80 mg.',
    contraindications: 'Active acute liver disease, unexplained persistent elevations in serum transaminases, pregnancy.',
    warnings: 'Instruct patient to report unexplained muscle pain or tea-colored urine.',
    mechanism: 'Inhibits HMG-CoA reductase, the rate-limiting enzyme in hepatic cholesterol synthesis; upregulates LDL receptors.',
    relatedDisease: 'hypertension',
    source: 'data.gov.in / NLEM',
    isIndiaMainApi: true,
  },
  {
    id: 'in-clopidogrel',
    slug: 'clopidogrel',
    name: 'Clopidogrel Bisulphate',
    genericName: 'CLOPIDOGREL BISULPHATE',
    class: 'Thienopyridine P2Y12 Antiplatelet Agent',
    category: 'Cardiology',
    subCategory: 'Antiplatelet',
    routes: ['Oral'],
    nlemTier: 'Primary, Secondary, Tertiary (NLEM Category 12.5)',
    cdscoStatus: 'Schedule H; Essential Antiplatelet for Acute Coronary Syndrome',
    nppaRegulated: true,
    desc: 'Irreversible platelet P2Y12 ADP receptor antagonist. Essential component of Dual Antiplatelet Therapy (DAPT) with Aspirin following coronary stent placement and acute myocardial infarction.',
    dose: 'ACS loading dose: 300 mg (or 600 mg prior to PCI) stat orally; maintenance: 75 mg once daily for 12 months with Aspirin.',
    caution: 'Bleeding risk (GI hemorrhage, intracranial). Prodrug bioactivation dependent on CYP2C19 (reduced activation with omeprazole; prefer pantoprazole).',
    indications: 'Acute coronary syndrome (STEMI and NSTEMI/UA), recent MI, recent ischemic stroke, peripheral arterial disease, post-percutaneous coronary intervention (PCI).',
    dosage: 'Tablets: 75 mg, 150 mg, 300 mg; Fixed-dose combination with Aspirin (75/75 mg, 75/150 mg).',
    dosageForms: 'Film-coated tablets: 75 mg; Loading dose tablets: 300 mg.',
    contraindications: 'Active pathological bleeding (peptic ulcer or intracranial hemorrhage); severe hepatic impairment.',
    warnings: 'Discontinue 5–7 days prior to elective major surgery to avoid excessive perioperative hemorrhage.',
    mechanism: 'Active metabolite irreversibly binds to platelet P2Y12 receptors, preventing ADP-mediated activation of the GPIIb/IIIa complex.',
    source: 'data.gov.in / NLEM',
    isIndiaMainApi: true,
  },
  {
    id: 'in-aspirin',
    slug: 'aspirin',
    name: 'Aspirin (Acetylsalicylic Acid)',
    genericName: 'ASPIRIN',
    class: 'Irreversible Cyclooxygenase Inhibitor / Antiplatelet',
    category: 'Cardiology',
    subCategory: 'Antiplatelet & Analgesic',
    routes: ['Oral'],
    nlemTier: 'Sub-Centre, Primary, Secondary, Tertiary (NLEM Category 12.5 & 2.1)',
    cdscoStatus: 'Universal Essential Cardiovascular Generic',
    nppaRegulated: true,
    desc: 'Irreversible inhibitor of platelet COX-1. The foundational antiplatelet agent for secondary cardiovascular prevention, acute coronary syndromes, and acute ischemic stroke.',
    dose: 'Acute MI loading: 162–325 mg chewed immediately; daily maintenance for secondary prevention: 75–100 mg once daily. Analgesic dose: 300–600 mg every 4–6 hours.',
    caution: 'GI bleeding and gastritis. Reye syndrome in children with viral infections. STRICTLY CONTRAINDICATED in Dengue fever (causes catastrophic hemorrhage).',
    indications: 'Secondary prevention of MI and ischemic stroke, acute coronary syndromes, acute ischemic stroke (within 48 hours), coronary bypass/stent prophylaxis.',
    dosage: 'Enteric-coated tablets: 75 mg, 100 mg, 150 mg; Dispersible tablets: 75 mg, 150 mg, 325 mg.',
    dosageForms: 'Enteric-coated tablets: 75 mg, 150 mg; Soluble/chewable tablets.',
    contraindications: 'Active peptic ulceration; hemophilia; children/adolescents under 16 with viral fever; suspected or confirmed Dengue fever.',
    warnings: 'In acute myocardial infarction, tablets must be CHEWED, not swallowed whole, for rapid buccal/gastric absorption.',
    mechanism: 'Irreversibly acetylates serine-529 on platelet COX-1, permanently inhibiting Thromboxane A2 (TXA2) production for platelet lifespan (7–10 days).',
    relatedDisease: 'dengue',
    source: 'data.gov.in / NLEM',
    isIndiaMainApi: true,
  },
  {
    id: 'in-furosemide',
    slug: 'furosemide',
    name: 'Furosemide',
    genericName: 'FUROSEMIDE',
    class: 'Loop Diuretic',
    category: 'Cardiology',
    subCategory: 'High-Ceiling Diuretic',
    routes: ['Oral', 'IV', 'IM'],
    nlemTier: 'Sub-Centre, Primary, Secondary, Tertiary (NLEM Category 16.1)',
    cdscoStatus: 'Schedule H; Essential Acute Decompensation Diuretic',
    nppaRegulated: true,
    desc: 'High-ceiling loop diuretic. The first-line emergency agent for volume overload in acute pulmonary edema, congestive heart failure decompensation, and acute renal failure.',
    dose: 'Acute pulmonary edema: 40–80 mg IV bolus stat over 2 minutes. Chronic heart failure maintenance: 20–40 mg orally once or twice daily.',
    caution: 'Hypokalemia, hypomagnesemia, hyponatremia, and dehydration. Ototoxicity at rapid high IV boluses.',
    indications: 'Acute pulmonary edema, congestive heart failure with fluid overload, chronic kidney disease edema, cirrhotic ascites (paired with spironolactone).',
    dosage: 'Tablets: 20 mg, 40 mg; Injection ampoules: 20 mg / 2 mL.',
    dosageForms: 'Tablets: 40 mg; Injection ampoules: 20 mg / 2 mL.',
    contraindications: 'Anuria unresponsive to test dose; severe electrolyte depletion; hepatic coma.',
    warnings: 'Monitor serum potassium, sodium, and creatinine frequently during active diuresis.',
    mechanism: 'Inhibits the Na+/K+/2Cl- cotransporter in the thick ascending limb of the loop of Henle, blocking 20–25% of filtered sodium reabsorption.',
    source: 'data.gov.in / NLEM',
    isIndiaMainApi: true,
  },
  {
    id: 'in-metoprolol',
    slug: 'metoprolol',
    name: 'Metoprolol Succinate / Tartrate',
    genericName: 'METOPROLOL',
    class: 'Cardioselective Beta-1 Adrenergic Receptor Blocker',
    category: 'Cardiology',
    subCategory: 'Beta-Blocker',
    routes: ['Oral', 'IV'],
    nlemTier: 'Primary, Secondary, Tertiary (NLEM Category 12.3)',
    cdscoStatus: 'Schedule H; Essential Cardioprotective Beta-Blocker',
    nppaRegulated: true,
    desc: 'Selective beta-1 adrenergic blocker. Proven mortality benefit in chronic heart failure (MERIT-HF trial) and essential secondary prevention following myocardial infarction.',
    dose: 'Heart failure (Succinate ER): start 12.5–25 mg once daily, titrate to target 200 mg once daily. Hypertension: 50–100 mg daily.',
    caution: 'Bradycardia and heart block; broncho-constriction in reactive airway disease; masks hypoglycemia warning signs (sweating/tremors) in diabetes.',
    indications: 'Chronic heart failure with reduced ejection fraction, post-myocardial infarction, angina pectoris, hypertension, supraventricular tachyarrhythmias.',
    dosage: 'Tablets (Tartrate IR): 25 mg, 50 mg; Tablets (Succinate ER): 25 mg, 50 mg, 100 mg; IV injection: 5 mg / 5 mL.',
    dosageForms: 'Immediate-release tablets; Extended-release tablets (Succinate); Ampoules for injection.',
    contraindications: 'Severe sinus bradycardia (HR < 50 bpm); 2nd or 3rd degree AV block; cardiogenic shock; decompensated heart failure exacerbation.',
    warnings: 'Do not discontinue abruptly; taper gradually over 1–2 weeks to avoid rebound tachycardia and ischemia.',
    mechanism: 'Competitively blocks beta-1 receptors in the myocardium, decreasing heart rate, contractility, and systemic renin secretion.',
    source: 'data.gov.in / NLEM',
    isIndiaMainApi: true,
  },

  // ----------------------------------------------------
  // SECTION 6: ENDOCRINE, DIABETES & METABOLISM
  // ----------------------------------------------------
  {
    id: 'in-metformin',
    slug: 'metformin',
    name: 'Metformin Hydrochloride',
    genericName: 'METFORMIN HYDROCHLORIDE',
    class: 'Biguanide Antidiabetic Agent',
    category: 'Endocrine',
    subCategory: 'Oral Antidiabetic',
    routes: ['Oral'],
    nlemTier: 'Primary Health Centre, CHC, District Hospital (NLEM Category 18.5)',
    cdscoStatus: 'Schedule H; Universal First-Line Antidiabetic in India',
    nppaRegulated: true,
    desc: 'The gold standard initial pharmacological therapy for Type 2 Diabetes Mellitus across India. Decreases hepatic gluconeogenesis and improves insulin sensitivity without causing hypoglycemia as monotherapy.',
    dose: 'Initial: 500 mg twice daily with meals or 850 mg once daily. Gradually titrate by 500 mg weekly to target (1500–2000 mg/day; max 2550 mg/day).',
    caution: 'Rare risk of Lactic Acidosis in severe renal impairment (eGFR <30 mL/min). Withhold prior to iodinated radiocontrast.',
    indications: 'Type 2 Diabetes Mellitus; Polycystic Ovary Syndrome (PCOS); prediabetes in high-risk individuals.',
    dosage: 'Tablets: 500 mg, 850 mg, 1000 mg; Extended Release: 500 mg, 750 mg, 1000 mg.',
    dosageForms: 'Immediate-release tablets; Extended-release (ER) tablets.',
    contraindications: 'Severe renal failure (eGFR <30 mL/min/1.73m²); metabolic acidosis; acute tissue hypoxia (shock, severe sepsis).',
    warnings: 'Gastrointestinal upset is minimized by taking with meals and slow upward dose titration.',
    mechanism: 'Activates AMP-activated protein kinase (AMPK) in hepatocytes, suppressing gluconeogenesis and increasing glucose disposal.',
    relatedDisease: 'diabetes',
    source: 'data.gov.in / ICMR / NLEM',
    isIndiaMainApi: true,
  },
  {
    id: 'in-glimepiride',
    slug: 'glimepiride',
    name: 'Glimepiride',
    genericName: 'GLIMEPIRIDE',
    class: 'Third-Generation Sulfonylurea',
    category: 'Endocrine',
    subCategory: 'Insulin Secretagogue',
    routes: ['Oral'],
    nlemTier: 'Primary, Secondary, Tertiary (NLEM Category 18.5)',
    cdscoStatus: 'Schedule H; Common Second-Line Antidiabetic in India',
    nppaRegulated: true,
    desc: 'Potent third-generation sulfonylurea insulin secretagogue. Frequently combined with Metformin when glycemic targets (HbA1c < 7%) are not achieved with monotherapy.',
    dose: 'Initial: 1 mg orally once daily with breakfast; titrate every 1–2 weeks based on blood glucose (usual: 2–4 mg once daily; max 8 mg/day).',
    caution: 'Hypoglycemia (highest in elderly or missed meals); weight gain. Educate patient on recognition and treatment of hypoglycemia.',
    indications: 'Type 2 Diabetes Mellitus as second-line add-on to Metformin or as monotherapy in metformin-intolerant patients.',
    dosage: 'Tablets: 1 mg, 2 mg, 3 mg, 4 mg; Fixed-dose combinations with Metformin.',
    dosageForms: 'Tablets: 1 mg, 2 mg, 3 mg, 4 mg; Combination tablets.',
    contraindications: 'Type 1 Diabetes Mellitus; diabetic ketoacidosis; severe renal or hepatic failure; hypersensitivity to sulfonylureas.',
    warnings: 'Instruct patients to always carry glucose tablets or sweets in case of hypoglycemic symptoms.',
    mechanism: 'Binds to sulfonylurea receptor 1 (SUR1) on pancreatic beta-cell membranes, closing ATP-sensitive K+ channels to stimulate insulin exocytosis.',
    relatedDisease: 'diabetes',
    source: 'data.gov.in / NLEM',
    isIndiaMainApi: true,
  },
  {
    id: 'in-insulin-regular',
    slug: 'insulin-regular',
    name: 'Insulin Human Regular (Soluble)',
    genericName: 'INSULIN HUMAN REGULAR',
    class: 'Short-Acting Recombinant Human Insulin',
    category: 'Endocrine',
    subCategory: 'Prandial & Emergency Insulin',
    routes: ['SC', 'IV', 'IM'],
    nlemTier: 'Primary Health Centre, CHC, District Hospital (NLEM Category 18.5)',
    cdscoStatus: 'Schedule G / Essential Lifesaving Antidiabetic Hormone',
    nppaRegulated: true,
    desc: 'Short-acting soluble recombinant human insulin. The only insulin formulation suitable for IV infusion in Diabetic Ketoacidosis (DKA) and hyperosmolar hyperglycemic state.',
    dose: 'DKA: 0.1 units/kg IV bolus followed by 0.1 units/kg/hour continuous infusion. Subcutaneous prandial: 0.1–0.2 units/kg per meal, 30 minutes before food.',
    caution: 'Hypoglycemia; hypokalemia (insulin drives potassium intracellularly). Rotate injection sites to prevent lipodystrophy.',
    indications: 'Type 1 Diabetes Mellitus, Type 2 Diabetes with secondary oral failure, Diabetic Ketoacidosis (DKA), emergency management of hyperkalemia.',
    dosage: 'Vials: 40 IU/mL (10 mL vial) and 100 IU/mL (10 mL vial); Cartridges: 100 IU/mL (3 mL).',
    dosageForms: 'Vials for injection: 40 IU/mL, 100 IU/mL; Cartridges for pens.',
    contraindications: 'Episodes of hypoglycemia; hypersensitivity to human insulin or metacresol formulation excipient.',
    warnings: 'Verify syringe calibration (U-40 vs U-100) to prevent 2.5-fold under- or overdosing errors.',
    mechanism: 'Binds to insulin tyrosine kinase receptor on skeletal muscle and adipocytes, promoting GLUT4 translocation and glucose uptake.',
    relatedDisease: 'diabetes',
    source: 'data.gov.in / NLEM',
    isIndiaMainApi: true,
  },
  {
    id: 'in-insulin-glargine',
    slug: 'insulin-glargine',
    name: 'Insulin Glargine',
    genericName: 'INSULIN GLARGINE',
    class: 'Long-Acting Basal Insulin Analog',
    category: 'Endocrine',
    subCategory: 'Basal Insulin Analog',
    routes: ['SC'],
    nlemTier: 'CHC, District Hospital, Medical College (NLEM Category 18.5)',
    cdscoStatus: 'Essential Long-Acting Basal Insulin Formulation',
    nppaRegulated: true,
    desc: 'Recombinant long-acting basal insulin analog with a flat, peakless 24-hour pharmacokinetic profile. Significantly lowers nocturnal hypoglycemia risk compared to NPH insulin.',
    dose: 'Initial in insulin-naive Type 2 Diabetes: 10 units (or 0.1–0.2 units/kg) subcutaneously once daily at the same time each evening; titrate to fasting glucose 80–130 mg/dL.',
    caution: 'Hypoglycemia. NEVER administer intravenously or intramuscularly. Do not mix with other insulins in the same syringe.',
    indications: 'Basal glycemic control in Type 1 Diabetes Mellitus (with prandial insulin) and Type 2 Diabetes Mellitus.',
    dosage: 'Vials: 100 IU/mL (10 mL); Pre-filled disposable pens and cartridges: 100 IU/mL (3 mL).',
    dosageForms: 'Vials: 100 IU/mL; Pre-filled pens: 100 IU/mL.',
    contraindications: 'During episodes of hypoglycemia; hypersensitivity.',
    warnings: 'Acidic pH (pH 4) causes microprecipitation in subcutaneous tissue; do not dilute or mix with neutral insulins.',
    mechanism: 'Microprecipitates in neutral subcutaneous tissue, dissolving slowly to release insulin monomers at a constant rate over 24 hours.',
    relatedDisease: 'diabetes',
    source: 'data.gov.in / NLEM',
    isIndiaMainApi: true,
  },
  {
    id: 'in-levothyroxine',
    slug: 'levothyroxine',
    name: 'Levothyroxine Sodium (T4)',
    genericName: 'LEVOTHYROXINE SODIUM',
    class: 'Synthetic Thyroid Hormone (T4)',
    category: 'Endocrine',
    subCategory: 'Thyroid Hormone Replacement',
    routes: ['Oral', 'IV'],
    nlemTier: 'Primary Health Centre, CHC, District Hospital (NLEM Category 18.3)',
    cdscoStatus: 'Schedule H; Essential Lifelong Thyroid Replacement Generic',
    nppaRegulated: true,
    desc: 'Synthetic crystalline L-thyroxine (T4). The universal lifelong replacement therapy for primary and central hypothyroidism across India.',
    dose: 'Adults: 1.6 mcg/kg/day (typical starting dose 50–100 mcg once daily). Elderly or coronary artery disease: start low at 12.5–25 mcg daily. Take on empty stomach 30–60 min before breakfast.',
    caution: 'Overtreatment leads to osteoporosis and atrial fibrillation. Absorption impaired by calcium, iron, PPIs, and soy; separate by 4 hours.',
    indications: 'Primary, secondary, and tertiary hypothyroidism; congenital hypothyroidism; TSH suppression in differentiated thyroid cancer.',
    dosage: 'Tablets: 12.5 mcg, 25 mcg, 50 mcg, 75 mcg, 88 mcg, 100 mcg, 112 mcg, 125 mcg, 150 mcg.',
    dosageForms: 'Tablets across microgram strengths; Powder for IV injection: 100 mcg, 200 mcg vials.',
    contraindications: 'Untreated subclinical or overt thyrotoxicosis; acute myocardial infarction without revascularization; uncorrected adrenal insufficiency.',
    warnings: 'In panhypopituitarism, always initiate corticosteroids BEFORE levothyroxine to prevent precipitating acute adrenal crisis.',
    mechanism: 'Deiodinated peripherally into active triiodothyronine (T3), which binds to nuclear thyroid receptors to regulate gene expression and basal metabolic rate.',
    source: 'data.gov.in / NLEM',
    isIndiaMainApi: true,
  },

  // ----------------------------------------------------
  // SECTION 7: GASTROINTESTINAL & ACID-PEPTIC MEDICINES
  // ----------------------------------------------------
  {
    id: 'in-ors',
    slug: 'oral-rehydration-salts',
    name: 'Oral Rehydration Salts (WHO / India Low-Osmolarity Formula)',
    genericName: 'ORAL REHYDRATION SALTS',
    class: 'Electrolyte Replenisher & Rehydration Formulation',
    category: 'Gastrointestinal',
    subCategory: 'Fluid & Electrolyte',
    routes: ['Oral'],
    nlemTier: 'Primary Health Centre, Sub-Centre, CHC, Hospital (NLEM Category 26.1)',
    cdscoStatus: 'Universal Over-The-Counter (OTC) Essential Formulation',
    nppaRegulated: true,
    desc: 'WHO/UNICEF reduced osmolarity oral rehydration formula (245 mOsm/L). Saves millions of lives across India by preventing hypovolemic dehydration in acute diarrhoeal diseases and cholera.',
    dose: 'Dissolve entire packet in exactly 1 Liter of clean drinking water. Give 50–100 mL after each loose stool for children <2 years; 100–200 mL for older children; ad libitum for adults.',
    caution: 'Do not boil prepared solution. Discard unused portion after 24 hours. In severe dehydration with shock, start IV fluid immediately.',
    indications: 'Acute gastroenteritis, cholera, pediatric diarrhea, heat exhaustion, viral enteritis.',
    dosage: 'Sachet for 1 Liter: NaCl 2.6g, Glucose 13.5g, KCl 1.5g, Trisodium citrate 2.9g (245 mOsm/L).',
    dosageForms: 'Sachets: 20.5g packet (for 1 Liter); 4.1g packet (for 200 mL).',
    contraindications: 'Paralytic ileus; severe intestinal obstruction; hemodynamic shock requiring IV resuscitation.',
    warnings: 'Do not add sugar or fruit juices, which worsen osmotic diarrhea.',
    mechanism: 'Utilizes sodium-glucose cotransporters (SGLT-1) in the small intestine to drive rapid water absorption.',
    relatedDisease: 'acute-diarrheal-disease',
    source: 'data.gov.in / CBHI / IDSP / NLEM',
    isIndiaMainApi: true,
  },
  {
    id: 'in-zinc-sulfate',
    slug: 'zinc-sulfate',
    name: 'Zinc Sulfate (Pediatric Diarrhea Protocol)',
    genericName: 'ZINC SULFATE DISPERSIBLE',
    class: 'Essential Trace Mineral / Mucosal Restorative',
    category: 'Gastrointestinal',
    subCategory: 'Pediatric Child Survival',
    routes: ['Oral'],
    nlemTier: 'Sub-Centre, Primary, Secondary, Tertiary (NLEM Category 27.2)',
    cdscoStatus: 'Essential Child Survival Medicine under National Health Mission (NHM)',
    nppaRegulated: true,
    desc: 'Essential micronutrient mandatory under Indian National Child Health Programme. Significantly reduces duration, severity, and recurrence of acute pediatric diarrhea.',
    dose: 'Infants <6 months: 10 mg elemental zinc once daily for 14 full days; Children 6 months to 5 years: 20 mg elemental zinc once daily for 14 full days.',
    caution: 'Ensure completion of full 14-day course even if diarrhea resolves early, to replenish mucosal zinc stores.',
    indications: 'Adjunct therapy to ORS in acute pediatric diarrhea; zinc deficiency states.',
    dosage: 'Dispersible tablet dissolved in water or breastmilk. 20 mg elemental zinc per day for 14 days.',
    dosageForms: 'Dispersible tablets: 10 mg, 20 mg elemental zinc; Oral syrup: 20 mg/5 mL.',
    contraindications: 'Hypersensitivity to zinc salts.',
    warnings: 'Administer after feeds to minimize nausea.',
    mechanism: 'Promotes mucosal brush-border enzyme regeneration and inhibits cAMP-dependent chloride secretion.',
    relatedDisease: 'acute-diarrheal-disease',
    source: 'data.gov.in / NHM / NLEM',
    isIndiaMainApi: true,
  },
  {
    id: 'in-pantoprazole',
    slug: 'pantoprazole',
    name: 'Pantoprazole Sodium',
    genericName: 'PANTOPRAZOLE SODIUM',
    class: 'Proton Pump Inhibitor (PPI)',
    category: 'Gastrointestinal',
    subCategory: 'Gastric Acid Suppressant',
    routes: ['Oral', 'IV'],
    nlemTier: 'Primary, Secondary, Tertiary (NLEM Category 17.1)',
    cdscoStatus: 'Schedule H; Essential Acid Peptic Disease Generic',
    nppaRegulated: true,
    desc: 'Irreversible proton pump inhibitor. Provides potent suppression of gastric acid. Least CYP2C19 interaction among PPIs, making it the preferred choice with Clopidogrel in cardiovascular patients.',
    dose: 'Oral: 40 mg once daily taken 30–60 minutes before breakfast. Upper GI Bleed: 80 mg IV bolus followed by 8 mg/hour infusion.',
    caution: 'Long-term usage (>1 year) associated with hypomagnesemia, vitamin B12 malabsorption, and C. diff diarrhea.',
    indications: 'GERD, peptic ulcer disease, stress ulcer prophylaxis, Zollinger-Ellison syndrome, acute upper GI bleeding.',
    dosage: 'Tablets: 20 mg, 40 mg; IV vial for injection: 40 mg.',
    dosageForms: 'Enteric-coated tablets: 20 mg, 40 mg; Lyophilized powder for injection: 40 mg.',
    contraindications: 'Hypersensitivity to substituted benzimidazoles; co-administration with rilpivirine.',
    warnings: 'Swallow tablet whole with water; do not crush or chew.',
    mechanism: 'Forms covalent disulfide bonds with H+/K+ ATPase in parietal cells, permanently inhibiting acid pumping.',
    relatedDisease: 'acute-diarrheal-disease',
    source: 'data.gov.in / NLEM',
    isIndiaMainApi: true,
  },
  {
    id: 'in-omeprazole',
    slug: 'omeprazole',
    name: 'Omeprazole',
    genericName: 'OMEPRAZOLE',
    class: 'Proton Pump Inhibitor (PPI)',
    category: 'Gastrointestinal',
    subCategory: 'Acid Suppressant',
    routes: ['Oral'],
    nlemTier: 'Sub-Centre, Primary, Secondary, Tertiary (NLEM Category 17.1)',
    cdscoStatus: 'Universal First-Line Gastric Acid Suppressant',
    nppaRegulated: true,
    desc: 'Prototypic proton pump inhibitor. Widely prescribed for peptic ulcer disease, H. pylori eradication therapy, and GERD across India.',
    dose: 'Standard: 20 mg orally once daily before breakfast for 4–8 weeks. Severe reflux: 40 mg once daily. H. pylori: 20 mg twice daily with amoxicillin and clarithromycin.',
    caution: 'Inhibits CYP2C19 (slows metabolism of diazepam, phenytoin, warfarin); risk of hypomagnesemia with prolonged use.',
    indications: 'Duodenal and gastric ulcers, gastroesophageal reflux disease, H. pylori eradication, NSAID-induced ulcer prophylaxis.',
    dosage: 'Capsules: 10 mg, 20 mg, 40 mg; Enteric-coated tablets.',
    dosageForms: 'Enteric-coated capsules: 20 mg, 40 mg.',
    contraindications: 'Hypersensitivity to PPIs; concurrent administration with nelfinavir.',
    warnings: 'Administer 30–60 minutes before morning meal for maximal efficacy.',
    mechanism: 'Inactivates H+/K+ ATPase enzyme system at secretory surface of gastric parietal cell.',
    source: 'data.gov.in / NLEM',
    isIndiaMainApi: true,
  },
  {
    id: 'in-ondansetron',
    slug: 'ondansetron',
    name: 'Ondansetron Hydrochloride',
    genericName: 'ONDANSETRON HYDROCHLORIDE',
    class: '5-HT3 Receptor Antagonist / Antiemetic',
    category: 'Gastrointestinal',
    subCategory: 'Serotonin Antagonist Antiemetic',
    routes: ['Oral', 'IV', 'IM'],
    nlemTier: 'Primary, Secondary, Tertiary (NLEM Category 17.2)',
    cdscoStatus: 'Schedule H; Essential Antiemetic for Post-Op & Chemotherapy',
    nppaRegulated: true,
    desc: 'Selective 5-hydroxytryptamine (5-HT3) receptor antagonist. Highly effective first-line antiemetic for chemotherapy-induced nausea/vomiting (CINV), post-operative vomiting, and acute pediatric gastroenteritis.',
    dose: 'Adults: 4–8 mg orally or slow IV push every 8 hours as needed. Children: 0.15 mg/kg per dose. CINV prophylaxis: 8–16 mg IV 30 min before chemo.',
    caution: 'Dose-dependent QT prolongation (max single IV dose 16 mg); constipation and transient headache are common adverse effects.',
    indications: 'Prevention and treatment of chemotherapy/radiotherapy-induced nausea and vomiting; post-operative nausea and vomiting; severe acute gastroenteritis vomiting.',
    dosage: 'Tablets: 4 mg, 8 mg; Orally disintegrating tablets: 4 mg, 8 mg; Syrup: 2 mg / 5 mL; Injection ampoules: 2 mg/mL (2 mL and 4 mL).',
    dosageForms: 'Film-coated tablets; Orally disintegrating tablets (ODT); Syrup; Ampoules for injection.',
    contraindications: 'Concomitant use of apomorphine (causes profound hypotension); congenital long QT syndrome.',
    warnings: 'Avoid in patients with severe hypokalemia or hypomagnesemia without correction.',
    mechanism: 'Blocks 5-HT3 receptors peripherally on vagal nerve terminals in the GI tract and centrally in the chemoreceptor trigger zone (CTZ).',
    source: 'data.gov.in / NLEM',
    isIndiaMainApi: true,
  },

  // ----------------------------------------------------
  // SECTION 8: RESPIRATORY MEDICINES
  // ----------------------------------------------------
  {
    id: 'in-salbutamol',
    slug: 'salbutamol',
    name: 'Salbutamol (Albuterol)',
    genericName: 'SALBUTAMOL SULPHATE',
    class: 'Short-Acting Beta-2 Adrenergic Agonist (SABA)',
    category: 'Respiratory',
    subCategory: 'Bronchodilator Reliever',
    routes: ['Inhalation', 'Oral', 'IV'],
    nlemTier: 'Sub-Centre, Primary, Secondary, Tertiary (NLEM Category 25.1)',
    cdscoStatus: 'Schedule H; Essential Quick-Relief Bronchodilator',
    nppaRegulated: true,
    desc: 'Rapid-acting selective beta-2 adrenoreceptor agonist. The universal first-line rescue medication for acute bronchospasm in asthma and COPD.',
    dose: 'Inhalation (MDI): 100–200 mcg (1–2 puffs) PRN for acute symptoms. Nebulization for acute severe asthma: 2.5–5 mg nebulized every 20 minutes for 3 doses.',
    caution: 'Fine skeletal muscle tremor and sinus tachycardia. Over-reliance (>2 canisters/year) without inhaled corticosteroid increases mortality risk.',
    indications: 'Acute bronchospasm in bronchial asthma, COPD exacerbations, exercise-induced asthma, emergency treatment of hyperkalemia.',
    dosage: 'MDI: 100 mcg per metered actuation; Respirator solution: 5 mg/mL (2.5 mg per respule); Tablets: 2 mg, 4 mg; Syrup: 2 mg/5 mL.',
    dosageForms: 'Metered Dose Inhaler (MDI); Nebulizer Respules: 2.5 mg; Tablets; Syrup.',
    contraindications: 'Hypersensitivity to salbutamol; threatened abortion in first two trimesters.',
    warnings: 'Always pair with an inhaled corticosteroid (Budesonide) for persistent asthma.',
    mechanism: 'Stimulates beta-2 adrenergic receptors, increasing cyclic AMP to relax bronchial smooth muscle.',
    relatedDisease: 'respiratory-infections',
    source: 'data.gov.in / NLEM',
    isIndiaMainApi: true,
  },
  {
    id: 'in-budesonide',
    slug: 'budesonide',
    name: 'Budesonide Inhaler',
    genericName: 'BUDESONIDE',
    class: 'Inhaled Corticosteroid (ICS)',
    category: 'Respiratory',
    subCategory: 'Asthma Controller',
    routes: ['Inhalation'],
    nlemTier: 'Primary Health Centre, CHC, District Hospital (NLEM Category 25.1)',
    cdscoStatus: 'Schedule H; First-Line Anti-Inflammatory Controller for Asthma',
    nppaRegulated: true,
    desc: 'Potent non-halogenated glucocorticoid with high topical anti-inflammatory potency and high first-pass hepatic metabolism (~90%). The foundational daily controller medication for persistent asthma.',
    dose: 'Adults: 200–400 mcg twice daily inhaled (mild-moderate asthma); severe: up to 800 mcg twice daily. Acute croup in children: 2 mg nebulized stat.',
    caution: 'Oropharyngeal candidiasis (oral thrush) and dysphonia. Instruct patient to rinse mouth and gargle with water and spit out after each inhalation.',
    indications: 'Maintenance treatment of persistent bronchial asthma; maintenance of chronic obstructive pulmonary disease; acute laryngotracheobronchitis (croup).',
    dosage: 'MDI: 100 mcg/puff, 200 mcg/puff; Dry powder inhaler (DPI): 100 mcg, 200 mcg, 400 mcg; Respules for nebulization: 0.5 mg / 2 mL, 1 mg / 2 mL.',
    dosageForms: 'Metered Dose Inhaler (MDI); Rotacaps/DPI; Nebulizer Respules: 0.5 mg, 1 mg.',
    contraindications: 'Primary treatment of status asthmaticus or other acute episodes where intensive measures are required.',
    warnings: 'Rinse mouth with water without swallowing after each dose to prevent hoarseness and oral fungal colonization.',
    mechanism: 'Crosses cell membranes and binds glucocorticoid receptors, down-regulating inflammatory cytokines (IL-4, IL-5) and reducing airway hyperresponsiveness.',
    source: 'data.gov.in / NLEM',
    isIndiaMainApi: true,
  },
  {
    id: 'in-ipratropium',
    slug: 'ipratropium-bromide',
    name: 'Ipratropium Bromide',
    genericName: 'IPRATROPIUM BROMIDE',
    class: 'Short-Acting Antimuscarinic Agent (SAMA)',
    category: 'Respiratory',
    subCategory: 'Anticholinergic Bronchodilator',
    routes: ['Inhalation'],
    nlemTier: 'Primary, Secondary, Tertiary (NLEM Category 25.1)',
    cdscoStatus: 'Schedule H; First-Line Bronchodilator for COPD Exacerbations',
    nppaRegulated: true,
    desc: 'Quaternary ammonium muscarinic acetylcholine receptor antagonist. Produces potent bronchodilation without systemic anticholinergic side effects.',
    dose: 'Nebulization in acute asthma/COPD: 500 mcg nebulized every 4–6 hours (often combined with salbutamol 2.5 mg). MDI: 20–40 mcg (1–2 puffs) 3–4 times daily.',
    caution: 'Dry mouth; metallic taste; risk of precipitating acute angle-closure glaucoma if nebulized aerosol mist leaks directly into patient eyes.',
    indications: 'Maintenance and acute exacerbations of Chronic Obstructive Pulmonary Disease (COPD); adjunct to SABA in acute severe asthma.',
    dosage: 'Metered Dose Inhaler: 20 mcg per puff; Respiratory solution for nebulization: 250 mcg / 2 mL, 500 mcg / 2 mL.',
    dosageForms: 'MDI: 20 mcg/actuation; Nebulizer respules: 250 mcg, 500 mcg; Combination respules with Levosalbutamol.',
    contraindications: 'Known hypersensitivity to ipratropium, atropine, or derivatives.',
    warnings: 'Use a mouthpiece or tightly fitting face mask during nebulization to shield eyes from anticholinergic aerosol.',
    mechanism: 'Blocks muscarinic M3 receptors on airway smooth muscle, inhibiting vagally-mediated cholinergic bronchoconstriction.',
    source: 'data.gov.in / NLEM',
    isIndiaMainApi: true,
  },

  // ----------------------------------------------------
  // SECTION 9: NEUROLOGY & PSYCHIATRY
  // ----------------------------------------------------
  {
    id: 'in-sertraline',
    slug: 'sertraline',
    name: 'Sertraline Hydrochloride',
    genericName: 'SERTRALINE HYDROCHLORIDE',
    class: 'Selective Serotonin Reuptake Inhibitor (SSRI)',
    category: 'Neurology',
    subCategory: 'Antidepressant & Anxiolytic',
    routes: ['Oral'],
    nlemTier: 'Primary Health Centre, CHC, District Hospital (NLEM Category 24.2)',
    cdscoStatus: 'Schedule H; First-Line Antidepressant Generic',
    nppaRegulated: true,
    desc: 'Potent and highly selective serotonin reuptake inhibitor. First-line therapy for major depressive disorder, generalized anxiety, panic disorder, and OCD with favorable cardiovascular safety.',
    dose: 'Initial: 50 mg orally once daily (morning or evening); may titrate after 2–4 weeks by 50 mg increments to max 200 mg once daily.',
    caution: 'Serotonin syndrome risk with MAOIs/tramadol; increased suicidal ideation in adolescents during initial weeks; discontinuation syndrome if stopped abruptly.',
    indications: 'Major depressive disorder, obsessive-compulsive disorder (OCD), panic disorder, post-traumatic stress disorder (PTSD), social anxiety disorder.',
    dosage: 'Tablets: 25 mg, 50 mg, 100 mg.',
    dosageForms: 'Film-coated tablets: 25 mg, 50 mg, 100 mg.',
    contraindications: 'Concomitant use of monoamine oxidase inhibitors (MAOIs); pimozide co-administration.',
    warnings: 'Allow at least 2–4 weeks for onset of antidepressant therapeutic efficacy; do not stop medication abruptly.',
    mechanism: 'Selectively inhibits presynaptic serotonin reuptake transporter (SERT), enhancing central 5-HT neurotransmission.',
    source: 'data.gov.in / NLEM',
    isIndiaMainApi: true,
  },
  {
    id: 'in-alprazolam',
    slug: 'alprazolam',
    name: 'Alprazolam',
    genericName: 'ALPRAZOLAM',
    class: 'Triazolobenzodiazepine',
    category: 'Neurology',
    subCategory: 'Anxiolytic',
    routes: ['Oral'],
    nlemTier: 'Primary, Secondary, Tertiary (NLEM Category 24.3)',
    cdscoStatus: 'Schedule H1 / NDPS Controlled Benzodiazepine',
    nppaRegulated: true,
    desc: 'Intermediate-acting triazolobenzodiazepine. Effective for short-term acute management of severe anxiety and panic disorder.',
    dose: 'Anxiety: 0.25–0.5 mg orally three times daily (max 4 mg/day in divided doses). Use lowest effective dose for shortest possible duration (≤2–4 weeks).',
    caution: 'High potential for psychological and physical dependence; severe rebound anxiety upon discontinuation; sedation; respiratory depression with opioids.',
    indications: 'Short-term relief of severe anxiety symptoms; acute panic disorder with or without agoraphobia.',
    dosage: 'Tablets: 0.25 mg, 0.5 mg, 1 mg; Extended-release tablets: 0.5 mg, 1 mg.',
    dosageForms: 'Tablets: 0.25 mg, 0.5 mg, 1 mg; Mouth dissolving tablets.',
    contraindications: 'Acute narrow-angle glaucoma; severe respiratory insufficiency; myasthenia gravis; sleep apnea syndrome.',
    warnings: 'Strict Schedule H1 register maintenance; taper dose gradually when discontinuing to avoid withdrawal seizures.',
    mechanism: 'Potentiates GABA-A receptor inhibitory neurotransmission by increasing chloride channel opening frequency.',
    source: 'data.gov.in / NLEM',
    isIndiaMainApi: true,
  },
  {
    id: 'in-phenytoin',
    slug: 'phenytoin',
    name: 'Phenytoin Sodium',
    genericName: 'PHENYTOIN SODIUM',
    class: 'Hydantoin Anticonvulsant / Sodium Channel Blocker',
    category: 'Neurology',
    subCategory: 'Antiepileptic',
    routes: ['Oral', 'IV'],
    nlemTier: 'Primary Health Centre, CHC, District Hospital (NLEM Category 5.1)',
    cdscoStatus: 'Schedule H; Essential First-Line Antiepileptic for Status Epilepticus',
    nppaRegulated: true,
    desc: 'Hydantoin class antiepileptic drug with non-linear saturation (Michaelis-Menten) kinetics. Foundational therapy for focal and generalized tonic-clonic seizures and status epilepticus.',
    dose: 'Status epilepticus loading: 15–20 mg/kg IV infusion (max rate 50 mg/minute in normal saline with ECG monitoring). Oral maintenance: 300–400 mg daily in 1–2 doses.',
    caution: 'Cardiac arrhythmias and hypotension if IV infused too rapidly (>50 mg/min). Gingival hyperplasia, hirsutism, cerebellar ataxia, nystagmus with chronic high levels.',
    indications: 'Generalized tonic-clonic seizures, focal seizures with or without secondary generalization, status epilepticus (second-line after benzodiazepine), neurosurgical prophylaxis.',
    dosage: 'Tablets/Capsules: 50 mg, 100 mg; Suspension: 30 mg / 5 mL, 125 mg / 5 mL; Ampoules for injection: 100 mg / 2 mL.',
    dosageForms: 'Capsules: 100 mg; Chewable tablets: 50 mg; Injection: 50 mg/mL in 2 mL ampoules.',
    contraindications: 'Sinus bradycardia, sino-atrial block, 2nd and 3rd degree AV block, Stokes-Adams syndrome.',
    warnings: 'Must be diluted in NORMAL SALINE only; precipitates instantly in dextrose solutions. Monitor blood pressure and ECG during IV loading.',
    mechanism: 'Promotes sodium efflux and stabilizes neuronal membranes by prolonging the inactivated state of voltage-gated sodium channels.',
    source: 'data.gov.in / NLEM',
    isIndiaMainApi: true,
  },
  {
    id: 'in-sodium-valproate',
    slug: 'sodium-valproate',
    name: 'Sodium Valproate / Valproic Acid',
    genericName: 'SODIUM VALPROATE',
    class: 'Broad-Spectrum Antiepileptic & Mood Stabilizer',
    category: 'Neurology',
    subCategory: 'Broad-Spectrum Anticonvulsant',
    routes: ['Oral', 'IV'],
    nlemTier: 'Primary, Secondary, Tertiary (NLEM Category 5.1 & 24.2)',
    cdscoStatus: 'Schedule H; Broad-Spectrum Antiepileptic for All Seizure Types',
    nppaRegulated: true,
    desc: 'Broad-spectrum antiepileptic effective against all major seizure types (generalized, focal, absence, myoclonic) and as a mood stabilizer in bipolar affective disorder.',
    dose: 'Initial: 15 mg/kg/day orally in 2 divided doses; titrate by 5–10 mg/kg/day weekly to therapeutic response (usual adult: 1000–2000 mg/day; max 60 mg/kg/day).',
    caution: 'HIGHLY TERATOGENIC (causes neural tube defects, spina bifida, and cognitive deficits in offspring); fatal hepatotoxicity in children <2 years; acute pancreatitis.',
    indications: 'Generalized tonic-clonic, absence, myoclonic, and focal seizures; manic episodes of bipolar disorder; migraine prophylaxis.',
    dosage: 'Tablets: 200 mg, 300 mg, 500 mg; Controlled Release (CR): 200 mg, 300 mg, 500 mg; Syrup: 200 mg / 5 mL; Injection vial: 500 mg.',
    dosageForms: 'Gastro-resistant tablets; Chrono/CR tablets: 300 mg, 500 mg; Syrup; IV injection.',
    contraindications: 'Pregnancy (unless no alternative antiepileptic available); acute or active liver disease; mitochondrial disorders caused by mutations in POLG.',
    warnings: 'Avoid in women of childbearing potential unless strictly enrolled in pregnancy prevention program.',
    mechanism: 'Increases brain GABA levels by inhibiting GABA transaminase and blocking voltage-gated sodium and T-type calcium channels.',
    source: 'data.gov.in / NLEM',
    isIndiaMainApi: true,
  },

  // ----------------------------------------------------
  // SECTION 10: EMERGENCY & RESUSCITATION MEDICINES
  // ----------------------------------------------------
  {
    id: 'in-epinephrine',
    slug: 'epinephrine',
    name: 'Adrenaline (Epinephrine)',
    genericName: 'EPINEPHRINE (ADRENALINE)',
    class: 'Direct-Acting Sympathomimetic Agonist',
    category: 'Emergency',
    subCategory: 'Resuscitation & Anaphylaxis First-Line',
    routes: ['IM', 'IV', 'SC', 'Endotracheal'],
    nlemTier: 'Sub-Centre, Primary Health Centre, CHC, Hospital (NLEM Category 1.1 & 3.1)',
    cdscoStatus: 'Universal Life-Saving Emergency Drug',
    nppaRegulated: true,
    desc: 'Endogenous catecholamine with potent alpha-1, beta-1, and beta-2 adrenergic agonist properties. The absolute first-line lifesaving medication for acute anaphylaxis and cardiac arrest resuscitation.',
    dose: 'Anaphylaxis: 0.5 mg (0.5 mL of 1:1,000 solution) IM into anterolateral thigh immediately; repeat every 5–15 min if needed. Cardiac Arrest: 1 mg (10 mL of 1:10,000 solution) IV/IO every 3–5 min.',
    caution: 'Tachyarrhythmias, severe hypertension, myocardial ischemia, and cerebral hemorrhage in overdose. Do NOT give 1:1,000 solution intravenously.',
    indications: 'Acute anaphylactic shock, cardiopulmonary resuscitation (cardiac arrest - VF, pulseless VT, asystole, PEA), severe acute asthma unresponsive to salbutamol.',
    dosage: 'Ampoules for injection: 1 mg / 1 mL (1:1,000 solution) for IM injection; 1:10,000 solution for IV cardiac resuscitation.',
    dosageForms: 'Ampoules: 1 mg/mL (1:1,000); Auto-injectors: 0.15 mg, 0.3 mg.',
    contraindications: 'No absolute contraindications in life-threatening anaphylaxis or cardiac arrest.',
    warnings: 'Anaphylaxis must be treated with INTRAMUSCULAR injection into mid-anterolateral thigh, NOT subcutaneous injection.',
    mechanism: 'Alpha-1 vasoconstriction reverses vasodilation and mucosal edema; beta-1 inotropy/chronotropy restores cardiac output; beta-2 bronchodilation relieves bronchospasm.',
    source: 'data.gov.in / NLEM',
    isIndiaMainApi: true,
  },
  {
    id: 'in-atropine',
    slug: 'atropine',
    name: 'Atropine Sulphate',
    genericName: 'ATROPINE SULPHATE',
    class: 'Competitive Muscarinic Acetylcholine Receptor Antagonist',
    category: 'Emergency',
    subCategory: 'Anticholinergic / Antidote',
    routes: ['IV', 'IM', 'SC', 'Ophthalmic'],
    nlemTier: 'Sub-Centre, Primary, Secondary, Tertiary (NLEM Category 1.1 & 4.1)',
    cdscoStatus: 'Schedule H; Essential Antidote for Organophosphate Poisoning in India',
    nppaRegulated: true,
    desc: 'Competitive muscarinic antagonist. Foundational emergency antidote for acute Organophosphate and Carbamate pesticide poisoning (a major cause of agricultural toxicological mortality across rural India).',
    dose: 'Hemodynamically unstable bradycardia: 0.5–1 mg IV push every 3–5 min (max 3 mg). Organophosphate poisoning: 2–5 mg IV every 10–15 min until complete atropinization (clearing of pulmonary secretions).',
    caution: 'Anticholinergic toxidrome (mydriasis, fever, dry mouth, urinary retention, delirium). Paradoxical bradycardia if given at subtherapeutic doses (<0.5 mg).',
    indications: 'Organophosphate and carbamate insecticide poisoning, symptomatic sinus bradycardia, pre-anesthetic drying of secretions, intraoperative vagal reflex reversal.',
    dosage: 'Injection ampoules: 0.6 mg / 1 mL, 1 mg / 1 mL; Ophthalmic drops: 1%.',
    dosageForms: 'Injection ampoules: 0.6 mg/mL, 1 mg/mL; Eye drops: 1%.',
    contraindications: 'Narrow-angle glaucoma; myasthenia gravis; pyloric stenosis (none in emergency organophosphate poisoning).',
    warnings: 'In pesticide poisoning, endpoint of atropinization is CLEARING of bronchosecretions and dry axillae, not pupil dilation.',
    mechanism: 'Competitively blocks acetylcholine at postganglionic muscarinic receptors in parasympathetic end organs.',
    source: 'data.gov.in / NLEM',
    isIndiaMainApi: true,
  },
  {
    "id": "in-rosuvastatin",
    "slug": "rosuvastatin",
    "name": "Rosuvastatin Calcium",
    "genericName": "ROSUVASTATIN CALCIUM",
    "class": "HMG-CoA Reductase Inhibitor (High-Intensity Statin)",
    "category": "Cardiology",
    "subCategory": "Lipid-Lowering Agent",
    "routes": [
      "Oral"
    ],
    "nlemTier": "Primary, Secondary, Tertiary (NLEM Category 12.6)",
    "cdscoStatus": "Schedule H; High-Efficacy Statin Generic",
    "nppaRegulated": true,
    "desc": "Hydrophilic high-intensity statin with potent LDL-C reduction (up to 55%) and favorable pharmacokinetic profile with minimal CYP3A4 interaction.",
    "dose": "Initial: 5–10 mg once daily; titrate every 4 weeks to 20 mg (max 40 mg for severe hypercholesterolemia). Asian ancestry: start at 5 mg.",
    "caution": "Myalgia and rare rhabdomyolysis. Higher plasma concentrations in Asian patients; initiate at 5 mg dose.",
    "indications": "Primary hypercholesterolemia, mixed dyslipidemia, hypertriglyceridemia, slowing progression of coronary atherosclerosis.",
    "dosage": "Tablets: 5 mg, 10 mg, 20 mg, 40 mg.",
    "dosageForms": "Film-coated tablets: 5 mg, 10 mg, 20 mg.",
    "contraindications": "Active liver disease; severe renal impairment (CrCl < 30 mL/min); pregnancy and breastfeeding.",
    "warnings": "Monitor fasting lipid panel at 4–12 weeks after initiation.",
    "mechanism": "Competitively inhibits HMG-CoA reductase, increasing cell-surface hepatic LDL receptors.",
    "source": "data.gov.in / NLEM",
    "isIndiaMainApi": true
  },
  {
    "id": "in-atenolol",
    "slug": "atenolol",
    "name": "Atenolol",
    "genericName": "ATENOLOL",
    "class": "Cardioselective Beta-1 Adrenergic Blocker",
    "category": "Cardiology",
    "subCategory": "Beta-Blocker",
    "routes": [
      "Oral",
      "IV"
    ],
    "nlemTier": "Sub-Centre, Primary, Secondary, Tertiary (NLEM Category 12.3)",
    "cdscoStatus": "Universal Essential Antihypertensive & Antianginal",
    "nppaRegulated": true,
    "desc": "Hydrophilic cardioselective beta-1 blocker. Widely prescribed across Indian public hospitals for angina pectoris and rate control in supraventricular arrhythmias.",
    "dose": "Hypertension: 25–50 mg orally once daily; titrate to 100 mg daily. Angina: 50–100 mg daily.",
    "caution": "Bradycardia and fatigue. Renally excreted; reduce dose in renal failure. Avoid abrupt discontinuation.",
    "indications": "Essential hypertension, chronic stable angina pectoris, post-MI secondary prevention, cardiac arrhythmias.",
    "dosage": "Tablets: 25 mg, 50 mg, 100 mg; Combination with Amlodipine (50/5 mg).",
    "dosageForms": "Tablets: 25 mg, 50 mg, 100 mg.",
    "contraindications": "Sinus bradycardia (HR < 50 bpm); 2nd/3rd degree AV block; cardiogenic shock; severe peripheral arterial disease.",
    "warnings": "Taper gradually over 1–2 weeks when stopping.",
    "mechanism": "Competitively blocks myocardial beta-1 adrenergic receptors, reducing resting and exercise heart rate.",
    "source": "data.gov.in / NLEM",
    "isIndiaMainApi": true
  },
  {
    "id": "in-carvedilol",
    "slug": "carvedilol",
    "name": "Carvedilol",
    "genericName": "CARVEDILOL",
    "class": "Non-Selective Beta-Blocker with Alpha-1 Vasodilatory Activity",
    "category": "Cardiology",
    "subCategory": "Heart Failure & Vasodilating Beta-Blocker",
    "routes": [
      "Oral"
    ],
    "nlemTier": "Primary, Secondary, Tertiary (NLEM Category 12.3)",
    "cdscoStatus": "Schedule H; Foundational Mortality-Reducing Drug in Heart Failure",
    "nppaRegulated": true,
    "desc": "Dual-action third-generation beta-blocker: non-selective beta-adrenergic blockade combined with alpha-1 vascular blockade. Significant survival benefit in systolic heart failure.",
    "dose": "Heart failure: start low at 3.125 mg twice daily with food; double dose every 2 weeks to target 25 mg twice daily (50 mg twice daily if >85 kg).",
    "caution": "Postural hypotension and dizziness (alpha-1 blockade); fluid retention during initial titration. Take with food to slow absorption.",
    "indications": "Chronic heart failure with reduced ejection fraction (NYHA II–IV), left ventricular dysfunction post-MI, hypertension.",
    "dosage": "Tablets: 3.125 mg, 6.25 mg, 12.5 mg, 25 mg.",
    "dosageForms": "Tablets: 3.125 mg, 6.25 mg, 12.5 mg, 25 mg.",
    "contraindications": "Decompensated heart failure requiring inotropes; bronchial asthma/bronchospasm; severe bradycardia; severe hepatic impairment.",
    "warnings": "Take with food to minimize the risk of orthostatic hypotension.",
    "mechanism": "Blocks beta-1 and beta-2 receptors, reducing myocardial oxygen demand; blocks alpha-1 receptors causing systemic vasodilation.",
    "source": "data.gov.in / NLEM",
    "isIndiaMainApi": true
  },
  {
    "id": "in-spironolactone",
    "slug": "spironolactone",
    "name": "Spironolactone",
    "genericName": "SPIRONOLACTONE",
    "class": "Aldosterone Receptor Antagonist / Potassium-Sparing Diuretic",
    "category": "Cardiology",
    "subCategory": "Mineralocorticoid Receptor Antagonist",
    "routes": [
      "Oral"
    ],
    "nlemTier": "Primary, Secondary, Tertiary (NLEM Category 16.1 & 12.4)",
    "cdscoStatus": "Schedule H; Essential Mortality-Reducing Drug in Heart Failure & Cirrhosis",
    "nppaRegulated": true,
    "desc": "Competitive aldosterone antagonist. Reduces cardiac fibrosis and all-cause mortality in heart failure (RALES trial) and serves as the drug of choice for cirrhotic ascites.",
    "dose": "Heart failure: 12.5–25 mg orally once daily (max 50 mg/day). Cirrhotic ascites: 100 mg once daily (titrated up to 400 mg with furosemide).",
    "caution": "Severe hyperkalemia (especially when combined with ACE inhibitors or ARBs); painful gynecomastia in men (antiandrogenic effect).",
    "indications": "Heart failure with reduced ejection fraction, cirrhotic ascites and edema, primary hyperaldosteronism, resistant hypertension.",
    "dosage": "Tablets: 25 mg, 50 mg, 100 mg.",
    "dosageForms": "Film-coated tablets: 25 mg, 50 mg, 100 mg.",
    "contraindications": "Severe renal impairment (eGFR < 30 mL/min); baseline serum potassium > 5.0 mEq/L; Addison disease.",
    "warnings": "Check serum potassium and creatinine at 1 week, 4 weeks, and every 3–6 months thereafter.",
    "mechanism": "Competitively blocks mineralocorticoid receptors in distal renal tubule, promoting sodium excretion and potassium retention.",
    "source": "data.gov.in / NLEM",
    "isIndiaMainApi": true
  },
  {
    "id": "in-digoxin",
    "slug": "digoxin",
    "name": "Digoxin",
    "genericName": "DIGOXIN",
    "class": "Cardiac Glycoside / Inotropic Agent",
    "category": "Cardiology",
    "subCategory": "Cardiac Inotrope & Rate Controller",
    "routes": [
      "Oral",
      "IV"
    ],
    "nlemTier": "Primary, Secondary, Tertiary (NLEM Category 12.4)",
    "cdscoStatus": "Schedule H; Narrow Therapeutic Index Glycoside",
    "nppaRegulated": true,
    "desc": "Purified cardiac glycoside extracted from Digitalis lanata. Increases myocardial contractility and slows ventricular rate in atrial fibrillation.",
    "dose": "Maintenance: 0.125–0.25 mg orally once daily. Elderly or renal impairment: 0.0625–0.125 mg once daily. Target serum level: 0.5–0.9 ng/mL.",
    "caution": "Narrow therapeutic index. Toxicity worsened by hypokalemia, hypomagnesemia, and renal failure. Symptoms: yellow-green halos (xanthopsia), nausea, ventricular ectopy.",
    "indications": "Ventricular rate control in chronic atrial fibrillation, refractory heart failure with reduced ejection fraction.",
    "dosage": "Tablets: 0.0625 mg, 0.25 mg; Pediatric elixir: 0.05 mg/mL; Ampoules: 0.5 mg / 2 mL.",
    "dosageForms": "Tablets: 0.25 mg; Ampoules for injection.",
    "contraindications": "Ventricular fibrillation; Wolff-Parkinson-White syndrome with AF; second or third-degree heart block; digitalis toxicity.",
    "warnings": "Monitor serum potassium strictly; hypokalemia dramatically increases cardiac digitalis toxicity.",
    "mechanism": "Inhibits myocardial Na+/K+ ATPase, increasing intracellular Na+ and Ca2+ to boost contractility; stimulates vagal tone to slow AV node.",
    "source": "data.gov.in / NLEM",
    "isIndiaMainApi": true
  },
  {
    "id": "in-nitroglycerin",
    "slug": "nitroglycerin",
    "name": "Nitroglycerin (Glyceryl Trinitrate)",
    "genericName": "NITROGLYCERIN",
    "class": "Organic Nitrate Vasodilator",
    "category": "Cardiology",
    "subCategory": "Antianginal Vasodilator",
    "routes": [
      "Sublingual",
      "IV",
      "Transdermal"
    ],
    "nlemTier": "Primary Health Centre, CHC, Hospital (NLEM Category 12.1)",
    "cdscoStatus": "Universal First-Line Emergency Antianginal",
    "nppaRegulated": true,
    "desc": "Potent systemic venodilator that generates nitric oxide. The universal first-line emergency medication for acute anginal chest pain and acute cardiogenic pulmonary edema.",
    "dose": "Acute angina: 0.4–0.5 mg sublingually dissolved under tongue every 5 minutes (max 3 tablets in 15 minutes). Acute pulmonary edema: IV infusion 5–10 mcg/min, titrate.",
    "caution": "Severe throbbing headache; profound hypotension. STRICTLY CONTRAINDICATED with PDE-5 inhibitors (sildenafil, tadalafil) within 24–48 hours (fatal collapse).",
    "indications": "Acute relief and prophylaxis of angina pectoris, acute coronary syndromes, acute decompensated heart failure with pulmonary congestion.",
    "dosage": "Sublingual tablets: 0.4 mg, 0.5 mg; Sublingual spray: 0.4 mg/metered dose; IV infusion: 5 mg/mL (5 mL, 10 mL vials).",
    "dosageForms": "Sublingual tablets; Sublingual spray; IV injection ampoules; Transdermal patches.",
    "contraindications": "Concurrent PDE-5 inhibitor use; right ventricular myocardial infarction; severe hypotension (SBP < 90 mmHg); hypertrophic cardiomyopathy.",
    "warnings": "Patient must sit down before taking sublingual tablet to avoid syncope from sudden orthostatic venodilation.",
    "mechanism": "Denitrated into nitric oxide (NO) which activates guanylyl cyclase, increasing cyclic GMP to cause venodilation and reduce cardiac preload.",
    "source": "data.gov.in / NLEM",
    "isIndiaMainApi": true
  },
  {
    "id": "in-gliclazide",
    "slug": "gliclazide",
    "name": "Gliclazide",
    "genericName": "GLICLAZIDE",
    "class": "Second-Generation Sulfonylurea",
    "category": "Endocrine",
    "subCategory": "Insulin Secretagogue",
    "routes": [
      "Oral"
    ],
    "nlemTier": "Primary, Secondary, Tertiary (NLEM Category 18.5)",
    "cdscoStatus": "Schedule H; Low-Hypoglycemia Risk Sulfonylurea",
    "nppaRegulated": true,
    "desc": "Second-generation sulfonylurea with selective pancreatic beta-cell binding and antioxidant properties. Lower hypoglycemia incidence compared to glibenclamide.",
    "dose": "Modified release (MR): 30–60 mg once daily with breakfast (max 120 mg once daily). Immediate release: 40–80 mg twice daily with meals.",
    "caution": "Hypoglycemia in patients with skipped meals or renal impairment; mild weight gain.",
    "indications": "Type 2 Diabetes Mellitus in adults when dietary measures and exercise are insufficient.",
    "dosage": "Tablets: 40 mg, 80 mg; Modified Release (MR) tablets: 30 mg, 60 mg.",
    "dosageForms": "Tablets: 80 mg; Modified-release tablets: 30 mg, 60 mg.",
    "contraindications": "Type 1 diabetes; diabetic ketoacidosis; severe renal or hepatic insufficiency; pregnancy and lactation.",
    "warnings": "Take with breakfast; never take on an empty stomach.",
    "mechanism": "Stimulates insulin secretion from pancreatic beta cells by closing ATP-sensitive potassium channels.",
    "relatedDisease": "diabetes",
    "source": "data.gov.in / NLEM",
    "isIndiaMainApi": true
  },
  {
    "id": "in-vildagliptin",
    "slug": "vildagliptin",
    "name": "Vildagliptin",
    "genericName": "VILDAGLIPTIN",
    "class": "Dipeptidyl Peptidase-4 (DPP-4) Inhibitor",
    "category": "Endocrine",
    "subCategory": "Incretin Enhancer",
    "routes": [
      "Oral"
    ],
    "nlemTier": "Primary, Secondary, Tertiary (NLEM Category 18.5)",
    "cdscoStatus": "Schedule H; Weight-Neutral Antidiabetic Generic",
    "nppaRegulated": true,
    "desc": "Potent and selective DPP-4 inhibitor that prolongs endogenous GLP-1 and GIP activity. Provides glucose-dependent insulin secretion without hypoglycemia or weight gain.",
    "dose": "Standard dose: 50 mg orally twice daily (morning and evening) in combination with metformin, or 50 mg once daily when paired with a sulfonylurea.",
    "caution": "Rare hepatic transaminase elevations. Monitor liver function tests at baseline and every 3 months for the first year.",
    "indications": "Type 2 Diabetes Mellitus in combination with Metformin, sulfonylureas, or insulin; monotherapy when metformin is contraindicated.",
    "dosage": "Tablets: 50 mg; Fixed-dose combinations with Metformin (50/500 mg, 50/850 mg, 50/1000 mg).",
    "dosageForms": "Tablets: 50 mg; Combination tablets.",
    "contraindications": "Hypersensitivity; hepatic impairment (baseline ALT/AST > 3x ULN).",
    "warnings": "Discontinue therapy if persistent elevated ALT or AST exceeds 3 times the upper limit of normal.",
    "mechanism": "Inhibits DPP-4 enzyme, preventing degradation of active incretin hormones (GLP-1 and GIP) to stimulate glucose-dependent insulin release.",
    "relatedDisease": "diabetes",
    "source": "data.gov.in / NLEM",
    "isIndiaMainApi": true
  },
  {
    "id": "in-dapagliflozin",
    "slug": "dapagliflozin",
    "name": "Dapagliflozin",
    "genericName": "DAPAGLIFLOZIN",
    "class": "Sodium-Glucose Cotransporter 2 (SGLT2) Inhibitor",
    "category": "Endocrine",
    "subCategory": "Cardiorenal Metabolic Agent",
    "routes": [
      "Oral"
    ],
    "nlemTier": "Primary, Secondary, Tertiary (NLEM Category 18.5)",
    "cdscoStatus": "Schedule H; Landmark Cardiorenal & Heart Failure Generic",
    "nppaRegulated": true,
    "desc": "Selective SGLT2 inhibitor. Induces renal glucosuria, promotes modest weight loss and blood pressure reduction, and provides proven heart failure and chronic kidney disease protection regardless of diabetes status.",
    "dose": "Standard dose: 10 mg orally once daily in the morning with or without food. Heart failure and CKD: 10 mg once daily.",
    "caution": "Mycotic genital infections (candidal vulvovaginitis / balanitis); volume depletion; rare Euglycemic Diabetic Ketoacidosis (euDKA) in surgical stress.",
    "indications": "Type 2 Diabetes Mellitus, Heart Failure with reduced or preserved ejection fraction, Chronic Kidney Disease progression delay.",
    "dosage": "Film-coated tablets: 5 mg, 10 mg; Fixed-dose combinations with Metformin.",
    "dosageForms": "Tablets: 5 mg, 10 mg; Combination tablets.",
    "contraindications": "Patients on dialysis; history of serious hypersensitivity reaction to dapagliflozin.",
    "warnings": "Instruct patients on good perineal hygiene and adequate daily hydration.",
    "mechanism": "Inhibits SGLT2 in the renal proximal convoluted tubule, blocking glucose reabsorption and promoting urinary glucose excretion (~70 g/day).",
    "relatedDisease": "diabetes",
    "source": "data.gov.in / NLEM",
    "isIndiaMainApi": true
  },
  {
    "id": "in-prednisolone",
    "slug": "prednisolone",
    "name": "Prednisolone",
    "genericName": "PREDNISOLONE",
    "class": "Glucocorticoid / Systemic Corticosteroid",
    "category": "Endocrine",
    "subCategory": "Anti-Inflammatory Corticosteroid",
    "routes": [
      "Oral"
    ],
    "nlemTier": "Primary Health Centre, CHC, District Hospital (NLEM Category 18.1)",
    "cdscoStatus": "Universal Essential Anti-Inflammatory & Immunosuppressive Generic",
    "nppaRegulated": true,
    "desc": "Intermediate-acting systemic glucocorticoid with 4 times the anti-inflammatory potency of hydrocortisone. First-line oral agent for acute asthma exacerbations, nephrotic syndrome, and autoimmune disorders.",
    "dose": "Acute asthma exacerbation: 40–50 mg orally once daily for 5 days. Nephrotic syndrome: 60 mg/m²/day (or 2 mg/kg/day) in divided doses.",
    "caution": "Hyperglycemia; hypertension; peptic ulceration; adrenal suppression with courses >2 weeks. Taper dose gradually after prolonged therapy.",
    "indications": "Severe acute asthma exacerbation, nephrotic syndrome, acute gout, rheumatoid arthritis flare-up, autoimmune hemolytic anemia.",
    "dosage": "Tablets: 5 mg, 10 mg, 20 mg, 40 mg; Dispersible tablets: 5 mg, 10 mg; Oral syrup: 5 mg / 5 mL.",
    "dosageForms": "Tablets: 5 mg, 10 mg, 20 mg; Dispersible tablets; Syrup.",
    "contraindications": "Systemic fungal infections; live or live-attenuated vaccines during immunosuppressive doses.",
    "warnings": "Do not discontinue abruptly after therapy lasting more than 14 days to prevent acute adrenal crisis.",
    "mechanism": "Binds intracellular glucocorticoid receptors, translocating to nucleus to repress transcription of pro-inflammatory genes.",
    "source": "data.gov.in / NLEM",
    "isIndiaMainApi": true
  },
  {
    "id": "in-rabeprazole",
    "slug": "rabeprazole",
    "name": "Rabeprazole Sodium",
    "genericName": "RABEPRAZOLE SODIUM",
    "class": "Proton Pump Inhibitor (PPI)",
    "category": "Gastrointestinal",
    "subCategory": "Gastric Acid Suppressant",
    "routes": [
      "Oral",
      "IV"
    ],
    "nlemTier": "Primary, Secondary, Tertiary (NLEM Category 17.1)",
    "cdscoStatus": "Schedule H; Rapid-Onset Acid Peptic Generic",
    "nppaRegulated": true,
    "desc": "Fastest-activating proton pump inhibitor. Provides rapid and sustained gastric acid suppression with minimal hepatic CYP2C19 genetic polymorphism dependence.",
    "dose": "Standard dose: 20 mg orally once daily in the morning before breakfast for 4–8 weeks. Severe GERD: 20 mg twice daily.",
    "caution": "Headache and diarrhea; prolonged use linked to vitamin B12 deficiency and hypomagnesemia.",
    "indications": "Active duodenal ulcer, gastric ulcer, gastroesophageal reflux disease (GERD), Zollinger-Ellison syndrome, H. pylori eradication.",
    "dosage": "Enteric-coated tablets: 10 mg, 20 mg; Fixed-dose combinations with Domperidone (20/30 mg SR).",
    "dosageForms": "Enteric-coated tablets: 20 mg; Sustained release capsules.",
    "contraindications": "Hypersensitivity to rabeprazole or substituted benzimidazoles.",
    "warnings": "Swallow tablet whole; do not chew or crush.",
    "mechanism": "Blocks the final step of gastric acid secretion by inhibiting the gastric H+/K+ ATPase at the secretory surface of the parietal cell.",
    "source": "data.gov.in / NLEM",
    "isIndiaMainApi": true
  },
  {
    "id": "in-domperidone",
    "slug": "domperidone",
    "name": "Domperidone",
    "genericName": "DOMPERIDONE",
    "class": "Peripheral Dopamine D2-Receptor Antagonist / Prokinetic",
    "category": "Gastrointestinal",
    "subCategory": "Prokinetic & Antiemetic",
    "routes": [
      "Oral"
    ],
    "nlemTier": "Primary, Secondary, Tertiary (NLEM Category 17.2)",
    "cdscoStatus": "Schedule H; First-Line Prokinetic for Dyspepsia & Reflux",
    "nppaRegulated": true,
    "desc": "Peripheral dopamine D2-receptor blocker that does not readily cross the blood-brain barrier. Enhances upper gastrointestinal motility and gastric emptying.",
    "dose": "Adults: 10 mg orally three times daily, 15–30 minutes before meals (max 30 mg/day). Use lowest effective dose for shortest duration (≤1 week).",
    "caution": "Cardiac arrhythmias and QT prolongation at doses >30 mg/day or in patients >60 years. Galactorrhea and amenorrhea from hyperprolactinemia.",
    "indications": "Nausea and vomiting, diabetic gastroparesis, functional dyspepsia, epigastric fullness and reflux.",
    "dosage": "Tablets: 10 mg; Dispersible tablets: 10 mg; Suspension: 5 mg / 5 mL; Fixed-dose combinations with PPIs.",
    "dosageForms": "Tablets: 10 mg; Oral suspension; Combination capsules.",
    "contraindications": "Prolonged cardiac conduction intervals (QTc); co-administration with strong CYP3A4 inhibitors; gastrointestinal hemorrhage or perforation; prolactinoma.",
    "warnings": "Do not exceed 30 mg/day; avoid in patients with underlying cardiac conduction disorders.",
    "mechanism": "Blocks peripheral dopamine D2 receptors in the upper GI tract, accelerating gastric emptying and duodenal transit.",
    "source": "data.gov.in / NLEM",
    "isIndiaMainApi": true
  },
  {
    "id": "in-loperamide",
    "slug": "loperamide",
    "name": "Loperamide Hydrochloride",
    "genericName": "LOPERAMIDE HYDROCHLORIDE",
    "class": "Synthetic Peripheral Opioid Receptor Agonist",
    "category": "Gastrointestinal",
    "subCategory": "Antidiarrheal Agent",
    "routes": [
      "Oral"
    ],
    "nlemTier": "Primary, Secondary, Tertiary (NLEM Category 17.5)",
    "cdscoStatus": "Universal Essential Antidiarrheal Generic",
    "nppaRegulated": true,
    "desc": "Peripheral mu-opioid receptor agonist that slows intestinal peristalsis without central opioid effects at therapeutic doses.",
    "dose": "Acute non-infectious diarrhea: 4 mg (2 capsules) orally stat, followed by 2 mg after each loose unformed stool (max 16 mg/day). Stop after 48 hours if no improvement.",
    "caution": "NEVER USE in acute infectious bloody diarrhea (dysentery) or high fever, as it delays clearance of invasive bacterial toxins (Shigella, Salmonella). Torsades de pointes in massive overdose.",
    "indications": "Symptomatic control of acute non-specific diarrhea, traveler’s diarrhea (afebrile, watery), chronic diarrhea associated with inflammatory bowel disease.",
    "dosage": "Capsules/Tablets: 2 mg.",
    "dosageForms": "Capsules: 2 mg; Tablets: 2 mg.",
    "contraindications": "Acute dysentery (bloody stools and high fever); acute ulcerative colitis; bacterial enterocolitis (Salmonella, Shigella, Campylobacter); pseudomembranous colitis.",
    "warnings": "Discontinue immediately if constipation, abdominal distension, or signs of toxic megacolon develop.",
    "mechanism": "Binds to mu-opioid receptors in the myenteric plexus of the large intestine, decreasing circular and longitudinal muscle transit.",
    "source": "data.gov.in / NLEM",
    "isIndiaMainApi": true
  },
  {
    "id": "in-montelukast",
    "slug": "montelukast",
    "name": "Montelukast Sodium",
    "genericName": "MONTELUKAST SODIUM",
    "class": "Cysteinyl Leukotriene Receptor Antagonist (LTRA)",
    "category": "Respiratory",
    "subCategory": "Leukotriene Modifier",
    "routes": [
      "Oral"
    ],
    "nlemTier": "Primary, Secondary, Tertiary (NLEM Category 25.1)",
    "cdscoStatus": "Schedule H; First-Line Add-on for Asthma & Allergic Rhinitis",
    "nppaRegulated": true,
    "desc": "Selective antagonist of the cysteinyl leukotriene CysLT1 receptor. Highly effective oral maintenance treatment for asthma with concurrent allergic rhinitis and exercise-induced bronchospasm.",
    "dose": "Adults and adolescents ≥15 years: 10 mg orally once daily in the evening. Children 6–14 years: 5 mg chewable tablet once daily; Children 2–5 years: 4 mg chewable.",
    "caution": "Neuropsychiatric adverse events (abnormal dreams, insomnia, anxiety, aggressive behavior, depression). Monitor patient behavior.",
    "indications": "Prophylaxis and chronic treatment of bronchial asthma, relief of allergic rhinitis symptoms (seasonal and perennial), exercise-induced bronchoconstriction.",
    "dosage": "Film-coated tablets: 10 mg; Chewable tablets: 4 mg, 5 mg; Granules: 4 mg sachet; Combination with Levocetirizine (10/5 mg).",
    "dosageForms": "Tablets: 10 mg; Chewable tablets; Combination tablets.",
    "contraindications": "Known hypersensitivity to montelukast.",
    "warnings": "Not indicated for the reversal of acute bronchospasm; always have a rescue SABA (salbutamol) available.",
    "mechanism": "Competitively blocks the CysLT1 receptor, inhibiting leukotriene-mediated airway edema, smooth muscle contraction, and cellular inflammation.",
    "source": "data.gov.in / NLEM",
    "isIndiaMainApi": true
  },
  {
    "id": "in-escitalopram",
    "slug": "escitalopram",
    "name": "Escitalopram Oxalate",
    "genericName": "ESCITALOPRAM OXALATE",
    "class": "Selective Serotonin Reuptake Inhibitor (Pure S-Enantiomer)",
    "category": "Neurology",
    "subCategory": "Antidepressant & Anxiolytic",
    "routes": [
      "Oral"
    ],
    "nlemTier": "Primary, Secondary, Tertiary (NLEM Category 24.2)",
    "cdscoStatus": "Schedule H; Highest Serotonin Selectivity SSRI",
    "nppaRegulated": true,
    "desc": "Pure S-enantiomer of citalopram. The most selective serotonin reuptake inhibitor available, providing fast and reliable relief of generalized anxiety and major depressive episodes with minimal side effects.",
    "dose": "Initial: 10 mg orally once daily (morning or evening); may increase to 20 mg once daily after at least 1–2 weeks. Elderly: 10 mg daily.",
    "caution": "Dose-dependent QT prolongation (max dose 20 mg/day, max 10 mg/day in elderly >65y); nausea, sexual dysfunction; discontinuation syndrome.",
    "indications": "Major depressive disorder, generalized anxiety disorder (GAD), panic disorder, social anxiety disorder.",
    "dosage": "Tablets: 5 mg, 10 mg, 20 mg; Fixed-dose combinations with Clonazepam (10/0.5 mg).",
    "dosageForms": "Film-coated tablets: 5 mg, 10 mg, 20 mg; Mouth dissolving tablets.",
    "contraindications": "Concomitant use with MAO inhibitors or pimozide; known congenital long QT syndrome.",
    "warnings": "Do not exceed 10 mg once daily in patients over 65 years of age due to QT prolongation risk.",
    "mechanism": "Allosterically binds the presynaptic serotonin transporter (SERT) at both primary and allosteric sites, selectively blocking 5-HT reuptake.",
    "source": "data.gov.in / NLEM",
    "isIndiaMainApi": true
  },
  {
    "id": "in-clonazepam",
    "slug": "clonazepam",
    "name": "Clonazepam",
    "genericName": "CLONAZEPAM",
    "class": "High-Potency Long-Acting Benzodiazepine",
    "category": "Neurology",
    "subCategory": "Anticonvulsant & Anxiolytic",
    "routes": [
      "Oral",
      "IV",
      "IM"
    ],
    "nlemTier": "Primary, Secondary, Tertiary (NLEM Category 5.1 & 24.3)",
    "cdscoStatus": "Schedule H1 / NDPS Regulated Anticonvulsant Generic",
    "nppaRegulated": true,
    "desc": "High-potency long-acting benzodiazepine with potent anticonvulsant, muscle relaxant, and anxiolytic actions.",
    "dose": "Panic disorder: start 0.25 mg twice daily; titrate to 1 mg/day (max 4 mg/day). Epilepsy: start 0.5 mg thrice daily, titrate to maintenance 4–8 mg/day.",
    "caution": "Sedation, cognitive slowing, physical and psychological dependence; severe withdrawal seizures if stopped abruptly.",
    "indications": "Panic disorder, absence seizures, myoclonic seizures, Lennox-Gastaut syndrome, adjunctive acute mania and akathisia.",
    "dosage": "Tablets: 0.25 mg, 0.5 mg, 1 mg, 2 mg; Mouth dissolving tablets: 0.25 mg, 0.5 mg; Injection ampoules: 1 mg / 1 mL.",
    "dosageForms": "Tablets across microgram/milligram strengths; Mouth-dissolving tablets.",
    "contraindications": "Severe hepatic impairment; significant respiratory depression; acute narrow-angle glaucoma.",
    "warnings": "Strict Schedule H1 register; taper very gradually (0.25 mg every 2 weeks) to avoid withdrawal delirium.",
    "mechanism": "Enhances inhibitory action of GABA by binding to benzodiazepine site on GABA-A receptor complex.",
    "source": "data.gov.in / NLEM",
    "isIndiaMainApi": true
  },
  {
    "id": "in-diazepam",
    "slug": "diazepam",
    "name": "Diazepam",
    "genericName": "DIAZEPAM",
    "class": "Long-Acting Benzodiazepine",
    "category": "Neurology",
    "subCategory": "Emergency Anticonvulsant & Sedative",
    "routes": [
      "IV",
      "Oral",
      "Rectal"
    ],
    "nlemTier": "Sub-Centre, Primary, Secondary, Tertiary (NLEM Category 1.3 & 5.1)",
    "cdscoStatus": "Universal First-Line Emergency Anticonvulsant",
    "nppaRegulated": true,
    "desc": "The universal first-line emergency drug for acute terminating of status epilepticus, febrile convulsions in children, and acute severe alcohol withdrawal across India.",
    "dose": "Status epilepticus: 10 mg slow IV push (over 2–3 minutes); repeat after 5–10 min if seizures continue (max 30 mg). Febrile seizure: 0.5 mg/kg rectally.",
    "caution": "Respiratory depression and hypotension if injected too rapidly. Active metabolites have very long elimination half-life (~100 hours).",
    "indications": "Status epilepticus, acute febrile seizures, severe alcohol withdrawal delirium, muscle spasm, premedication for cardioversion.",
    "dosage": "Tablets: 2 mg, 5 mg, 10 mg; Injection ampoules: 10 mg / 2 mL; Rectal gel tubes.",
    "dosageForms": "Tablets: 5 mg, 10 mg; Ampoules for injection: 10 mg / 2 mL.",
    "contraindications": "Severe acute respiratory depression; acute narrow-angle glaucoma; myasthenia gravis.",
    "warnings": "Inject into large veins slowly (≤5 mg/minute); ensure bag-valve-mask resuscitation is available at bedside.",
    "mechanism": "Facilitates GABA inhibitory neurotransmission across the central nervous system, terminating epileptogenic firing.",
    "source": "data.gov.in / NLEM",
    "isIndiaMainApi": true
  },
  {
    "id": "in-cetirizine",
    "slug": "cetirizine",
    "name": "Cetirizine Hydrochloride",
    "genericName": "CETIRIZINE HYDROCHLORIDE",
    "class": "Second-Generation Non-Sedating H1-Antihistamine",
    "category": "Respiratory",
    "subCategory": "Antihistamine",
    "routes": [
      "Oral"
    ],
    "nlemTier": "Sub-Centre, Primary, Secondary, Tertiary (NLEM Category 3.1)",
    "cdscoStatus": "Universal Over-The-Counter Essential Antiallergic Generic",
    "nppaRegulated": true,
    "desc": "Second-generation piperazine H1-receptor antagonist. Rapid onset of action with minimal blood-brain barrier penetration, providing effective relief of urticaria and allergic rhinitis.",
    "dose": "Adults and children ≥12 years: 10 mg orally once daily (evening preferred). Children 6–11 years: 5 mg once daily or 2.5 mg twice daily.",
    "caution": "Mild drowsiness can occur in ~10% of sensitive patients; avoid concurrent alcohol or central sedatives.",
    "indications": "Allergic rhinitis (sneezing, rhinorrhea, nasal itching), chronic idiopathic urticaria, insect bite reactions, allergic conjunctivitis.",
    "dosage": "Tablets: 5 mg, 10 mg; Syrup: 5 mg / 5 mL; Drops: 10 mg/mL.",
    "dosageForms": "Film-coated tablets: 10 mg; Oral syrup; Drops.",
    "contraindications": "Severe end-stage renal disease (CrCl < 10 mL/min); hypersensitivity to hydroxyzine.",
    "warnings": "Adjust dose in moderate-to-severe renal impairment (5 mg every other day).",
    "mechanism": "Competitively blocks peripheral H1 histamine receptors, preventing histamine-induced capillary permeability and wheal formation.",
    "source": "data.gov.in / NLEM",
    "isIndiaMainApi": true
  },
  {
    "id": "in-tranexamic-acid",
    "slug": "tranexamic-acid",
    "name": "Tranexamic Acid",
    "genericName": "TRANEXAMIC ACID",
    "class": "Synthetic Antifibrinolytic Agent",
    "category": "Emergency",
    "subCategory": "Hemostatic Agent",
    "routes": [
      "Oral",
      "IV"
    ],
    "nlemTier": "Primary Health Centre, CHC, District Hospital (NLEM Category 10.2)",
    "cdscoStatus": "Schedule H; Essential Lifesaving Antifibrinolytic for PPH & Trauma",
    "nppaRegulated": true,
    "desc": "Synthetic lysine analog antifibrinolytic. Proved survival benefit in traumatic hemorrhage (CRASH-2 trial) and postpartum hemorrhage (WOMAN trial) across Indian obstetric units.",
    "dose": "Postpartum hemorrhage / Trauma: 1 g IV infusion over 10 minutes within 3 hours of onset; repeat second dose of 1 g if bleeding continues after 30 minutes. Menorrhagia: 1000 mg orally thrice daily.",
    "caution": "Risk of thromboembolism if given inappropriately. Must be administered within 3 hours of trauma or delivery (harmful if given >3h post-injury).",
    "indications": "Postpartum hemorrhage (PPH), severe acute traumatic bleeding, surgical hemorrhage, heavy menstrual bleeding, dental extraction in hemophilia.",
    "dosage": "Tablets: 500 mg; Injection ampoules: 500 mg / 5 mL, 1000 mg / 10 mL.",
    "dosageForms": "Tablets: 500 mg; Injection ampoules: 100 mg/mL.",
    "contraindications": "Active acute thromboembolic disease (DVT, PE); active subarachnoid hemorrhage (risk of cerebral ischemia); color vision deficiency.",
    "warnings": "In trauma and postpartum bleeding, give immediately within the first 3 hours; do NOT delay.",
    "mechanism": "Competitively inhibits plasminogen activation by binding to its lysine-binding sites, preventing fibrin degradation and clot dissolution.",
    "source": "data.gov.in / NLEM",
    "isIndiaMainApi": true
  },
  {
    "id": "in-mannitol",
    "slug": "mannitol",
    "name": "Mannitol Infusion",
    "genericName": "MANNITOL",
    "class": "Osmotic Diuretic",
    "category": "Emergency",
    "subCategory": "Neuro-Critical Care Osmotic Agent",
    "routes": [
      "IV"
    ],
    "nlemTier": "Community Health Centre, District Hospital, Medical College (NLEM Category 16.1)",
    "cdscoStatus": "Schedule H; Essential Emergency Drug for Raised Intracranial Pressure",
    "nppaRegulated": true,
    "desc": "Hexahydric alcohol osmotic diuretic. The emergency first-line agent for acute cerebral edema, elevated intracranial pressure (ICP) in head injury/stroke, and acute angle-closure glaucoma.",
    "dose": "Elevated intracranial pressure: 0.25–1 g/kg (as 20% solution) IV infusion over 20–30 minutes. Repeat every 6–8 hours as needed with serum osmolality monitoring.",
    "caution": "Precipitates acute pulmonary edema and congestive heart failure in compromised cardiac reserve; severe dehydration and electrolyte imbalances.",
    "indications": "Acute cerebral edema with impending brain herniation, head trauma with elevated ICP, acute angle-closure glaucoma crisis.",
    "dosage": "IV Infusion bottles: 10% (500 mL), 20% (100 mL, 350 mL, 500 mL).",
    "dosageForms": "Glass/plastic infusion bottles: 20% w/v (200 mg/mL).",
    "contraindications": "Well-established severe anuria; severe pulmonary edema or congestive heart failure; active intracranial bleeding (except during craniotomy).",
    "warnings": "Inspect bottle for crystals before infusion; if crystals present, warm bottle in warm water to redissolve.",
    "mechanism": "Increases plasma osmolality, drawing free water down osmotic gradient from parenchymal brain tissue and intraocular space into vascular compartment.",
    "source": "data.gov.in / NLEM",
    "isIndiaMainApi": true
  },
  {
    "id": "in-naloxone",
    "slug": "naloxone",
    "name": "Naloxone Hydrochloride",
    "genericName": "NALOXONE HYDROCHLORIDE",
    "class": "Pure Opioid Receptor Antagonist",
    "category": "Emergency",
    "subCategory": "Opioid Overdose Antidote",
    "routes": [
      "IV",
      "IM",
      "SC",
      "Intranasal"
    ],
    "nlemTier": "Primary Health Centre, CHC, Hospital (NLEM Category 4.2)",
    "cdscoStatus": "Universal Life-Saving Opioid Antidote",
    "nppaRegulated": true,
    "desc": "Pure competitive antagonist at mu, kappa, and delta opioid receptors. Reverses life-threatening respiratory depression and coma from natural and synthetic opioid overdose in seconds.",
    "dose": "Acute opioid overdose: 0.4–2 mg IV/IM every 2–3 minutes until spontaneous respiratory drive is restored. Repeat doses required due to short half-life (30–90 min).",
    "caution": "Precipitates acute opioid withdrawal syndrome in physically dependent individuals (vomiting, diaphoresis, severe agitation). Half-life is shorter than most opioids; patient may re-narcotize.",
    "indications": "Emergency reversal of known or suspected opioid-induced respiratory depression and coma; diagnosis of suspected opioid overdose.",
    "dosage": "Injection ampoules: 0.4 mg / 1 mL; Nasal spray: 4 mg.",
    "dosageForms": "Ampoules for injection: 0.4 mg/mL; Pre-filled syringe; Nasal spray.",
    "contraindications": "Known hypersensitivity to naloxone hydrochloride.",
    "warnings": "Continuous observation is essential for at least 4 hours post-reversal because opioid duration of action often exceeds that of naloxone.",
    "mechanism": "Competitively displaces opioid agonists from mu-opioid receptors in the central nervous system, restoring spontaneous respiration.",
    "source": "data.gov.in / NLEM",
    "isIndiaMainApi": true
  }
];

/**
 * Search the Indian Health & Essential Drug Registry (data.gov.in / CBHI / IDSP)
 * Matches generic drug names, disease terms, and epidemiological surveillance queries.
 */
export function searchIndiaHealthRegistry(query) {
  if (!query || typeof query !== 'string') return [];
  const qClean = query.toLowerCase().trim();
  if (!qClean) return [];

  const matchedCards = [];
  const seenSlugs = new Set();

  // 1. Direct match on NLEM generics
  for (const drug of INDIA_NLEM_CATALOG) {
    const nameMatch = drug.name.toLowerCase().includes(qClean);
    const genericMatch = drug.genericName.toLowerCase().includes(qClean);
    const classMatch = (drug.class || '').toLowerCase().includes(qClean);
    const indMatch = (drug.indications || '').toLowerCase().includes(qClean);
    const descMatch = (drug.desc || '').toLowerCase().includes(qClean);
    const relatedMatch = (drug.relatedDisease || '').toLowerCase().includes(qClean);

    if (nameMatch || genericMatch || classMatch || indMatch || descMatch || relatedMatch) {
      if (!seenSlugs.has(drug.slug)) {
        seenSlugs.add(drug.slug);
        const card = enrichDrugWithIndiaPublicHealth(drug);
        matchedCards.push(card);
      }
    }
  }

  // 2. Check if query matches a CBHI / IDSP Disease Profile
  for (const [diseaseKey, profile] of Object.entries(CBHI_DISEASE_PROFILES)) {
    const keyMatch = diseaseKey.includes(qClean);
    const disMatch = profile.disease.toLowerCase().includes(qClean);
    const vectorMatch = (profile.vector || '').toLowerCase().includes(qClean);

    if (keyMatch || disMatch || vectorMatch) {
      for (const genericKeyword of profile.firstLineGenerics || []) {
        const foundDrug = INDIA_NLEM_CATALOG.find(
          (d) => d.slug.includes(genericKeyword) || d.name.toLowerCase().includes(genericKeyword)
        );
        if (foundDrug && !seenSlugs.has(foundDrug.slug)) {
          seenSlugs.add(foundDrug.slug);
          const card = enrichDrugWithIndiaPublicHealth(foundDrug);
          card.matchedViaDisease = profile.disease;
          matchedCards.push(card);
        }
      }
    }
  }

  return matchedCards;
}

/**
 * Look up complete Indian Public Health & Prescribing Profile for a drug slug
 */
export function getIndiaHealthMonograph(slug) {
  if (!slug) return null;
  const clean = slug.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  const found = INDIA_NLEM_CATALOG.find((d) => d.slug === clean || d.slug.includes(clean) || clean.includes(d.slug));
  if (!found) return null;
  return enrichDrugWithIndiaPublicHealth(found);
}

/**
 * Enrich a drug entry with official CBHI epidemiology & IDSP surveillance data
 */
export function enrichDrugWithIndiaPublicHealth(drug) {
  const relatedDiseaseKey = drug.relatedDisease;
  const cbhiProfile = relatedDiseaseKey ? CBHI_DISEASE_PROFILES[relatedDiseaseKey] : null;
  const idspProfile = relatedDiseaseKey ? IDSP_SURVEILLANCE_PROTOCOLS[relatedDiseaseKey] : null;

  return {
    ...drug,
    isIndiaMainApi: true,
    sourceTag: '🇮🇳 data.gov.in | CBHI | IDSP',
    sources: [
      { name: 'data.gov.in (National List of Essential Medicines)', url: 'https://data.gov.in' },
      { name: 'Central Bureau of Health Intelligence (CBHI)', url: 'http://cbhi-hsp.gov.in' },
      { name: 'Integrated Disease Surveillance Programme (IDSP / NCDC)', url: 'https://idsp.nic.in' },
    ],
    cbhiStats: cbhiProfile || null,
    idspSurveillance: idspProfile || null,
    nlemBadge: drug.nlemTier ? `NLEM: ${drug.nlemTier}` : 'NLEM India',
  };
}
